import React, { useMemo, useRef, useState, useEffect } from 'react'
import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import { CampaignDetailResponse, FormField } from '../core/_models'
import {
  importLeads, getSubmissionsByCampaign, saveOutboundCall, triggerCallInitiate,
  deleteDataSubmission, deleteMultipleDataSubmissions, updateLeadStatus, getData, toggleAutoDial, toggleVoiceBlast,
  bulkUpdateStatus, bulkAssignAgent, getAssignedAgents, getDashboard,
  updateData, togglePredictiveStatus, resetPredictiveData, resetVoiceBlastData,
  testCall, transferLeads
} from '../core/_requests'
import Swal from 'sweetalert2'
import * as XLSX from 'xlsx'
import { MonitoringPredictive } from '../../components/MonitoringPredictive'
import clsx from 'clsx'
import { 
  Trash2, 
  Search, 
  Filter, 
  Database, 
  Upload, 
  Download, 
  PhoneCall, 
  PhoneOutgoing, 
  ArrowLeftRight, 
  UserPlus, 
  RefreshCw, 
  Play, 
  Pause, 
  Check, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  ChevronUp, 
  ChevronDown, 
  Sparkles,
  Phone,
  Clock,
  Layers,
  Activity,
  CheckCircle2,
  XCircle,
  Users
} from 'lucide-react'
import { useThemeMode } from '../../../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import { useAuth } from '../../../../../modules/auth'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import * as signalR from '@microsoft/signalr'
import axios from 'axios'
import { CampaignDashboard } from './CampaignDashboard'
import { useAgentContext } from '../../AgentContext'

type Props = {
  data: CampaignDetailResponse
  externalTicketTrigger?: { phone: string, timestamp: number } | null
  onTriggerProcessed?: () => void
}

export const CampaignDetailInfo = ({ data, externalTicketTrigger, onTriggerProcessed }: Props) => {

  const { currentUser } = useAuth();
  const userRoles = currentUser?.roles || [];
  const isAgent = userRoles.some((r: any) =>
    (r?.name || r?.roleName || r || '').toString().toLowerCase().includes('agent')
  );
  const { campaignMetrics } = useAgentContext();
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [importPreview, setImportPreview] = useState<any[]>([])
  const [realData, setRealData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalData, setTotalData] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const [activeTab, setActiveTab] = useState<'all' | 'close'>('all')
  const [agentViewMode, setAgentViewMode] = useState<'kanban' | 'list' | 'table'>('list')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState<any | null>(null)
  const [dynamicResults, setDynamicResults] = useState<Record<string, string>>({})

  const [predictiveSettings, setPredictiveSettings] = useState({
    pacingRatio: data.pacingRatio || 1,
    phoneCategory: data.phoneCategory || '',
    durationPerTouch: data.durationPerTouch || '',
    retries: data.retries || 0,
    acwTime: data.acwTime || '',
    predictiveWaitSound: data.predictiveWaitSound || 'ringing',
  })
  const [isPredictiveActive, setIsPredictiveActive] = useState(data.autoDialActive || false)
  const [remainingDataCount, setRemainingDataCount] = useState(0)

  useEffect(() => {
    if (selectedLead) {
      setDynamicResults(selectedLead);
    } else {
      setDynamicResults({});
    }
  }, [selectedLead]);

  const [isLoadingStats, setIsLoadingStats] = useState(true)
  const [iconColorClass, setIconColorClass] = useState('text-primary')
  const [activeFormTab, setActiveFormTab] = useState<string>('')
  const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>(null)
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [searchStatus, setSearchStatus] = useState('')
  // Auto Dial state
  const [autoDialActive, setAutoDialActive] = useState<boolean>(data.autoDialActive ?? false)
  const [voiceBlastActive, setVoiceBlastActive] = useState<boolean>(data.voiceBlastActive ?? false)
  const [autoDialLoading, setAutoDialLoading] = useState(false)
  const [autoDialDelay, setAutoDialDelay] = useState<number>(data.autoDialDelaySecs ?? 5)
  // Map submissionId -> realtime disposition for live table coloring
  const [realtimeDisposition, setRealtimeDisposition] = useState<Record<string, { status: string, disposition: string }>>({})

  // Test Call State
  const [isTestCallModalOpen, setIsTestCallModalOpen] = useState(false)
  const [testCallTimeout, setTestCallTimeout] = useState(30)
  const [isTestCalling, setIsTestCalling] = useState(false)

  // Transfer Leads State
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false)
  const [transferTargetId, setTransferTargetId] = useState('')
  const [transferStatusFilter, setTransferStatusFilter] = useState('')
  const [isTransferring, setIsTransferring] = useState(false)
  const [availableCampaigns, setAvailableCampaigns] = useState<any[]>([])

  const [callStartTime, setCallStartTime] = useState<number | null>(null);
  const [callDurationStr, setCallDurationStr] = useState<string>('00:00');

  useEffect(() => {
    let interval: any = null;
    const currentSubId = selectedLead ? String(selectedLead.id) : null;
    const currentDisp = currentSubId ? realtimeDisposition[currentSubId]?.disposition : null;

    if (currentDisp === 'ANSWERED') {
      setCallStartTime(prev => {
        const start = prev || Date.now();
        interval = setInterval(() => {
          const diff = Math.floor((Date.now() - start) / 1000);
          const m = Math.floor(diff / 60).toString().padStart(2, '0');
          const s = (diff % 60).toString().padStart(2, '0');
          setCallDurationStr(`${m}:${s}`);
        }, 1000);
        return start;
      });
    } else {
      if (currentDisp === 'CALLING') {
        setCallStartTime(null);
        setCallDurationStr('00:00');
      }
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [realtimeDisposition, selectedLead]);

  useEffect(() => {
    const handleSoftphoneEnded = () => {
      setRealtimeDisposition((prev: any) => {
        const subId = Object.keys(prev).find(id => prev[id]?.disposition === 'CALLING' || prev[id]?.disposition === 'ANSWERED');
        if (subId) {
          return {
            ...prev,
            [subId]: { status: 'completed', disposition: 'COMPLETED' }
          };
        }
        return prev;
      });
    };

    window.addEventListener('softphone-call-ended', handleSoftphoneEnded);

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        const btn = document.getElementById('btn-save-call-result');
        if (btn) {
          btn.click();
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      window.removeEventListener('softphone-call-ended', handleSoftphoneEnded);
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!isDrawerOpen) {
      setCallStartTime(null);
      setCallDurationStr('00:00');
    }
  }, [isDrawerOpen]);

  // Stats for the active campaign
  const [dashboardStats, setDashboardStats] = useState<any>(null);

  const fetchDashboardStats = async () => {
    if (!data.id) return;
    try {
      const stats = await getDashboard(data.id as string);
      setDashboardStats(stats);
    } catch (e) {
      console.warn("Failed to fetch dashboard stats", e);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, [data.id]);

  const sortedFields = useMemo(() => {
    const baseFields: FormField[] = data?.formFields ?? []
    return [...baseFields].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
  }, [data?.formFields])

  const callFieldName = useMemo(() => {
    return sortedFields.find((f: any) => f.canCall)?.fieldName || sortedFields.find((f: any) => f.fieldType === 'Phone')?.fieldName || sortedFields[0]?.fieldName || '';
  }, [sortedFields])

  const groupedFields = useMemo(() => {
    const editable = sortedFields.filter(f => f.canEdit)
    return editable.reduce((acc, field) => {
      const group = field.fieldGroup || 'OTHER_INFO'
      if (!acc[group]) acc[group] = []
      acc[group].push(field)
      return acc
    }, {} as Record<string, typeof editable>)
  }, [sortedFields])

  useEffect(() => {
    const groups = Object.keys(groupedFields)
    if (groups.length > 0 && !groups.includes(activeFormTab)) {
      setActiveFormTab(groups[0])
    }
  }, [groupedFields, activeFormTab])

  useEffect(() => {
    const hour = new Date().getHours()
    if (hour >= 18 || hour < 6) {
      setIconColorClass('text-warning')
    } else {
      setIconColorClass('text-primary')
    }
  }, [])

  // SignalR connection for real-time auto-dial updates
  useEffect(() => {
    if (data.dialMode !== 'Auto' && data.dialMode !== 'Predictive') return

    const apiUrl = process.env.REACT_APP_THEME_API_URL
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiUrl}/statushub`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        accessTokenFactory: () => {
          const raw = localStorage.getItem('kt-auth-react-v');
          if (!raw) return '';
          try { return JSON.parse(raw).api_token || ''; } catch { return ''; }
        }
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) // Retry logic if connection drops
      .build()

    // Configure timeouts to prevent "Server timeout elapsed" on SSE or slow networks
    connection.serverTimeoutInMilliseconds = 120000; // 2 minutes
    connection.keepAliveIntervalInMilliseconds = 15000; // 15 seconds

    connection.on('callStatusUpdated', (payload: any) => {
      if (!payload || payload.campaignId?.toLowerCase() !== String(data.id)?.toLowerCase()) return

      const subId = String(payload.submissionId)
      setRealtimeDisposition(prev => ({
        ...prev,
        [subId]: { status: payload.status, disposition: payload.disposition }
      }))

      // Show toast when dialer originates a call (if we are an agent)
      if (payload.status === 'calling' && payload.disposition !== 'ANSWERED' && isAgent && data.dialMode !== 'Predictive') {
        if (!payload.assignedTo || String(payload.assignedTo) === String(currentUser?.id)) {
          const msg = `Auto Dialing: ${payload.name || payload.phoneNumber || 'Data baru'}...`
          if (!toast.isActive(`dial-${subId}`)) {
            toast.info(msg, { toastId: `dial-${subId}`, autoClose: 4000 })
          }
        }
      }

      // Auto-open drawer when customer is connected to agent (ANSWERED) — only for agents
      if (payload.disposition === 'ANSWERED' && isAgent) {
        if (payload.assignedTo && String(payload.assignedTo) !== String(currentUser?.id)) {
          return // Assigned to another agent, ignore pop-up
        }

        setRealData(prev => {
          const matchingLead = prev.find(l => String(l.id) === subId)
          if (matchingLead) {
            setSelectedLead(matchingLead)
            setIsDrawerOpen(true)
          } else {
            // Fallback: fetch the lead directly if not in current page's view
            getSubmissionsByCampaign(data.id as string, `search=${subId}&items_per_page=1`).then(res => {
              const fetched = res?.data?.[0]
              if (fetched) {
                setSelectedLead(fetched)
                setIsDrawerOpen(true)
              }
            }).catch(err => console.warn('[AutoDial] Screen pop fetch error:', err))
          }
          return prev
        })
      }

      // If it's a final status, refresh the actual table data after a short delay
      if (payload.status !== 'calling' && payload.status !== 'in-call') {
        setTimeout(() => fetchLeads(currentPage), 1500)
      }
    })

    let startPromise = connection.start().catch(e => console.warn('[SignalR] AutoDial hub error:', e));
    return () => {
      startPromise.then(() => connection.stop());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.id, data.dialMode])


  useEffect(() => {
    if (externalTicketTrigger && realData.length > 0) {
      const matchingLead = realData.find(l => {
        const phone = typeof l.phone_number === 'string' ? l.phone_number : String(l.phone_number ?? '')
        const trigger = typeof externalTicketTrigger.phone === 'string' ? externalTicketTrigger.phone : String(externalTicketTrigger.phone ?? '')
        return phone && trigger && (phone.includes(trigger) || trigger.includes(phone))
      })
      if (matchingLead) {
        setSelectedLead(matchingLead)
        setIsDrawerOpen(true)
        if (onTriggerProcessed) onTriggerProcessed()
      }
    }
  }, [externalTicketTrigger, realData, onTriggerProcessed])

  const fetchLeads = async (page: number = 1, limit: number = pageSize, sort?: { key: string, direction: string } | null) => {
    if (!data.id) return
    setLoading(true)
    try {
      const tabParam = activeTab === 'close' ? '&status=close' : ''
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''
      const statusParam = searchStatus ? `&status_submission=${encodeURIComponent(searchStatus)}` : ''
      const currentSort = sort !== undefined ? sort : sortConfig
      const response = await getSubmissionsByCampaign(data.id as string, `page=${page}&items_per_page=${limit}${tabParam}${searchParam}${statusParam}`, currentSort?.key, currentSort?.direction)
      setRealData(response.data || [])
      setTotalData(response.total || 0)
      setCurrentPage(page)
    } catch (e) {
      console.error("Gagal fetch leads:", e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLeads(1, pageSize, sortConfig)
  }, [data.id, pageSize, activeTab, sortConfig, searchStatus])

  // Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchLeads(1, pageSize, sortConfig)
    }, 500)
    return () => clearTimeout(handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  const totalPages = Math.ceil(totalData / pageSize)

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = Number(e.target.value)
    setPageSize(newSize)
    setCurrentPage(1)
  }

  const handleExportTemplate = () => {
    if (sortedFields.length === 0) return Swal.fire('Error', 'Field belum dikonfigurasi', 'error')
    const headers = [[...sortedFields.map((f) => f.fieldName || '')]]
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet(headers)
    XLSX.utils.book_append_sheet(wb, ws, 'Template')
    XLSX.writeFile(wb, `Template_Leads_${data.name.replace(/\s+/g, '_')}.xlsx`)
  }

  const handleImportLeads = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (fileInputRef.current) fileInputRef.current.value = ''
    const reader = new FileReader()
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result
        const wb = XLSX.read(bstr, { type: 'binary' })
        const dataJson: any[] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]])

        if (dataJson.length === 0) return Swal.fire('Error', 'File kosong', 'error')

        Swal.fire({ title: 'Memeriksa duplikasi...', didOpen: () => Swal.showLoading(), allowOutsideClick: false })
        const existingResponse = await getSubmissionsByCampaign(data.id as string, `all=true`)
        const existingLeads = existingResponse?.data || [];
        const excelFields = dataJson.length > 0 ? Object.keys(dataJson[0]) : [];

        const uniqueFieldNames = sortedFields.filter(f => f.isUnique).map(f => f.fieldName);
        const fieldsToCheck = uniqueFieldNames.length > 0 ? uniqueFieldNames : excelFields;

        const createFingerprint = (obj: any, fieldsToFields: string[]) => {
          return JSON.stringify(
            fieldsToFields
              .sort()
              .reduce((acc: any, key) => {
                // If key is undefined, skip
                if (!key) return acc;
                const val = obj[key] !== null && obj[key] !== undefined ? obj[key] : '';
                acc[key] = String(val).trim().toLowerCase();
                return acc;
              }, {})
          );
        };

        // For duplicate checking, we must check internal data with internal fields, but excel might have different casing.
        // Assuming excel header names match fieldNames exactly.
        const existingFingerprints = new Set(
          existingLeads.map((item: any) => createFingerprint(item, fieldsToCheck as string[]))
        );

        const uniqueData = dataJson.filter(newItem => {
          // If no unique fields are defined at all, and we default to checking all fields, then use fieldsToCheck.
          // If there are unique fields, use those to determine uniqueness.
          const newFingerprint = createFingerprint(newItem, fieldsToCheck as string[]);
          return !existingFingerprints.has(newFingerprint);
        });

        const duplicateCount = dataJson.length - uniqueData.length
        if (uniqueData.length === 0) {
          return Swal.fire('Info', 'Semua data di file sudah ada di database (Duplikat).', 'info')
        }

        setImportPreview(uniqueData.slice(0, 5))

        Swal.fire({
          title: 'Pratinjau Berhasil',
          html: `Total data: <b>${dataJson.length}</b><br/>` +
            `Data baru: <b style="color: green">${uniqueData.length}</b><br/>` +
            `Data duplikat diabaikan: <b style="color: red">${duplicateCount}</b><br/><br/>` +
            `Lanjutkan upload data baru?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Ya, Upload'
        }).then((res) => {
          if (res.isConfirmed) {
            handleConfirmUpload(uniqueData)
          } else {
            setImportPreview([])
            if (fileInputRef.current) fileInputRef.current.value = ''
          }
        })

      } catch (err) {
        console.error(err)
        Swal.fire('Error', 'Gagal memproses data', 'error')
      }
    }
    reader.readAsBinaryString(file)
  }

  const deleteSubmission = useMutation((id: any) => deleteDataSubmission(id), {
    onSuccess: (_, id) => {
      toast.success('Data deleted successfully')
      setRealData(prev => prev.filter(item => String(item.id) !== String(id)))
      setTotalData(prev => Math.max(0, prev - 1))
      fetchLeads(currentPage)
      fetchDashboardStats()
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to delete data')
      fetchLeads(currentPage)
    }
  })

  const handleDelete = async (id: any) => {
    // Optimistically remove immediately from table
    setRealData(prev => prev.filter(item => String(item.id) !== String(id)))
    setTotalData(prev => Math.max(0, prev - 1))
    await deleteSubmission.mutateAsync(id)
  }

  const handleToggleAutoDial = async (active: boolean) => {
    if (!data.id) return
    setAutoDialLoading(true)
    try {
      await toggleAutoDial(String(data.id), active, autoDialDelay)
      setAutoDialActive(active)
      if (active) {
        toast.success(
          data.dialMode === 'Predictive'
            ? 'Predictive Dial diaktifkan! Sistem mulai mendial leads.'
            : 'Auto Dial diaktifkan! Sistem mulai mendial leads secara berurutan.',
          { toastId: 'auto-dial-on' }
        )
      } else {
        toast.info(
          data.dialMode === 'Predictive'
            ? 'Predictive Dial dinonaktifkan.'
            : 'Auto Dial dinonaktifkan.',
          { toastId: 'auto-dial-off' }
        )
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gagal mengubah status Auto Dial')
    } finally {
      setAutoDialLoading(false)
    }
  }

  const handleToggleVoiceBlast = async (active: boolean) => {
    if (!data.id) return
    setAutoDialLoading(true)
    try {
      await toggleVoiceBlast(String(data.id), active)
      setVoiceBlastActive(active)
      if (active) {
        toast.success('Voice Blast mulai berjalan.')
      } else {
        toast.warning('Voice Blast dihentikan.')
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Gagal mengubah Voice Blast')
    } finally {
      setAutoDialLoading(false)
    }
  }

  const handleConfirmUpload = async (allData: any[]) => {
    Swal.fire({ title: 'Uploading...', didOpen: () => Swal.showLoading() })
    try {
      await importLeads(data.id as string, allData)
      Swal.fire('Berhasil', 'Data masuk antrian.', 'success')
      setImportPreview([])
      if (fileInputRef.current) fileInputRef.current.value = ''
      fetchLeads(1)
    } catch (error: any) { Swal.fire('Gagal', 'Error server', 'error') }
  }

  const openCallDrawer = async (lead: any, fieldName: string) => {
    const maxRetries = data.retries ?? 3;
    if (lead.call_count >= maxRetries) {
      Swal.fire({
        title: 'Batas Panggilan Tercapai',
        text: `Lead ini sudah dihubungi sebanyak ${maxRetries} kali. Tidak diperbolehkan melakukan panggilan ke-${maxRetries + 1}.`,
        icon: 'warning',
        confirmButtonColor: '#6366f1'
      })
      return
    }

    setSelectedLead(lead)

    setRealtimeDisposition(prev => ({
      ...prev,
      [lead.id]: { status: 'calling', disposition: 'CALLING' }
    }))

    try {
      await triggerCallInitiate(data.id as string, lead.id, fieldName)
    } catch (e: any) {
      Swal.fire('Gagal Call', e.response?.data?.message || 'Pastikan Extension Anda Aktif', 'error')
    }
  }

  const callClosed = async (lead: any, fieldName: string) => {
    setSelectedLead(lead)
    try {
      await triggerCallInitiate(data.id as string, lead.id, fieldName)
      Swal.close()
      // setIsDrawerOpen(true) 
    } catch (e: any) {
      Swal.fire('Gagal Call', e.response?.data?.message || 'Pastikan Extension Anda Aktif', 'error')
    }
  }

  const handleSaveCallResult = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedLead?.id) return
    setLoading(true)
    try {
      await saveOutboundCall({
        campaignId: data.id as string,
        submissionId: selectedLead.id,
        webhookResults: dynamicResults,
        notes: ''
      })
      setDynamicResults({})
      setIsDrawerOpen(false)
      fetchLeads(currentPage)
    } catch (err: any) {
      Swal.fire('Gagal', err || 'Gagal simpan data', 'error')
    } finally {
      setLoading(false)
    }
  }

  const displayData = useMemo(() => {
    return importPreview.length > 0 ? [...importPreview] : [...realData]
  }, [importPreview, realData])

  const groupedData = useMemo(() => {
    const groups: Record<string, any[]> = {
      'Ready': [],
      'Close': []
    };
    displayData.forEach(row => {
      const stat = row.status_submission || 'Ready';
      const matchingKey = Object.keys(groups).find(k => k.toLowerCase() === stat.toLowerCase());
      if (matchingKey) {
        groups[matchingKey].push(row);
      } else {
        groups['Ready'].push(row);
      }
    });
    return groups;
  }, [displayData]);

  const extractPolicyNumber = (leadObj: any) => {
    if (!leadObj) return '';
    if (leadObj.SubmissionValues && typeof leadObj.SubmissionValues === 'string') {
      try {
        const parsed = JSON.parse(leadObj.SubmissionValues);
        const possibleJsonKeys = [
          'nopolis', 'nopolicy', 'policyno', 'policynumber',
          'nomorpolis', 'insurancenumber', 'insuranceno',
          'nopol', 'nop', 'nopoli', 'polisno', 'policynum'
        ];
        const foundJsonKey = Object.keys(parsed).find(key => {
          const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
          return possibleJsonKeys.includes(normalized);
        });
        if (foundJsonKey && parsed[foundJsonKey]) return String(parsed[foundJsonKey]);
      } catch (e) { }
    }
    const possibleRootKeys = [
      'nopolis', 'nopolicy', 'policyno', 'policynumber',
      'nomorpolis', 'insurancenumber', 'insuranceno',
      'nopol', 'nop', 'nopoli', 'polisno', 'policynum'
    ];
    const foundRootKey = Object.keys(leadObj).find(key => {
      const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      return possibleRootKeys.includes(normalized);
    });
    return foundRootKey ? String(leadObj[foundRootKey]) : '';
  };

  useEffect(() => {
  }, [isDrawerOpen, selectedLead]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
    // fetchLeads will be triggered by useEffect on sortConfig change
  }

  const renderSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) return <i className='bi bi-arrow-down-up ms-1 text-white opacity-50 fs-9'></i>
    return sortConfig.direction === 'asc' ? <i className='bi bi-arrow-up ms-1 text-white fw-bold fs-9'></i> : <i className='bi bi-arrow-down ms-1 text-white fw-bold fs-9'></i>
  }

  const toggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedLeads(new Set(displayData.map(d => String(d.id))))
    } else {
      setSelectedLeads(new Set())
    }
  }

  const toggleSelectLead = (id: string) => {
    setSelectedLeads(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleBulkStatus = async () => {
    const ids = Array.from(selectedLeads)
    const { value: status } = await Swal.fire({
      title: 'Ganti Status Massal',
      input: 'select',
      inputOptions: {
        'ready': 'Ready',
        'close': 'Close'
      },
      inputPlaceholder: 'Pilih status baru',
      showCancelButton: true
    })

    if (status) {
      setLoading(true)
      try {
        await bulkUpdateStatus(ids, status)
        Swal.fire('Berhasil', 'Status berhasil diperbarui', 'success')
        setSelectedLeads(new Set())
        fetchLeads(currentPage)
      } catch (e: any) {
        Swal.fire('Gagal', e.message || 'Gagal update status', 'error')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleBulkAssign = async () => {
    const ids = Array.from(selectedLeads)
    setLoading(true)
    try {
      const response = await getAssignedAgents(data.id as string)
      setLoading(false)

      const agentsList = response?.data?.data || []
      const agentOptions = { '': 'Unassign (Kosongkan)' } as Record<string, string>
      agentsList.forEach((a: any) => {
        agentOptions[String(a.Id || a.id)] = a.Nama || a.nama || a.Username || a.username
      })

      const { value: agentId } = await Swal.fire({
        title: 'Assign Agent Massal',
        input: 'select',
        inputOptions: agentOptions,
        inputPlaceholder: 'Pilih Agent',
        showCancelButton: true
      })

      if (agentId !== undefined) {
        setLoading(true)
        await bulkAssignAgent(ids, agentId === '' ? null : agentId)
        Swal.fire('Berhasil', 'Agent berhasil di-assign', 'success')
        setSelectedLeads(new Set())
        fetchLeads(currentPage)
      }
    } catch (e: any) {
      setLoading(false)
      Swal.fire('Gagal', e.message || 'Gagal mengambil data agent atau assign', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedLeads)
    const { isConfirmed } = await Swal.fire({
      title: 'Hapus Leads?',
      text: `Anda yakin ingin menghapus ${ids.length} leads terpilih? (Leads yang sudah di-assign ke Agent tidak akan ikut terhapus)`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444'
    })

    if (isConfirmed) {
      setLoading(true)
      try {
        const response = await deleteMultipleDataSubmissions(ids)
        Swal.fire('Berhasil', response?.Message || response?.message || 'Leads berhasil dihapus', 'success')
        setSelectedLeads(new Set())
        setRealData(prev => prev.filter(item => !ids.includes(String(item.id))))
        setTotalData(prev => Math.max(0, prev - ids.length))
        fetchLeads(currentPage)
        fetchDashboardStats()
      } catch (e: any) {
        const errMsg = e?.response?.data?.message || typeof e?.response?.data === 'string' ? e.response.data : e.message
        Swal.fire('Gagal', errMsg || 'Gagal menghapus leads', 'error')
      } finally {
        setLoading(false)
      }
    }
  }

  const renderFormBody = () => (
    <form id='kt_call_result_form' onSubmit={handleSaveCallResult}>
      {/* ── ACTIVE CALL BANNER ── */}
      {(() => {
        const currentSubId = selectedLead ? String(selectedLead.id) : null;
        const currentDisp = currentSubId ? realtimeDisposition[currentSubId]?.disposition : null;

        if (currentDisp !== 'CALLING' && currentDisp !== 'ANSWERED') return null;

        return (
          <div className='mb-5 rounded-4 position-relative overflow-hidden d-flex align-items-center justify-content-between px-5 py-4'
            style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(59,130,246,0.05) 100%)',
              border: '1.5px solid rgba(16,185,129,0.25)',
              boxShadow: '0 2px 12px rgba(16,185,129,0.08)',
            }}>
            <div>
              <div className='d-flex align-items-center gap-3 mb-1'>
                <span className='position-relative d-flex' style={{ width: 8, height: 8 }}>
                  <span className='animate-ping position-absolute h-100 w-100 rounded-circle' style={{ background: '#10b981', opacity: 0.7 }}></span>
                  <span className='rounded-circle h-100 w-100' style={{ background: '#10b981' }}></span>
                </span>
                <h4 className='fw-bolder text-body mb-0 font-monospace' style={{ letterSpacing: '0.02em' }}>{selectedLead?.phone_number}</h4>
              </div>
              {currentDisp === 'CALLING' ? (
                <span className='fw-bold' style={{ fontSize: 11, color: '#f59e0b', letterSpacing: '0.8px', textTransform: 'uppercase' }}>● Ringing...</span>
              ) : (
                <span className='fw-bold' style={{ fontSize: 11, color: '#10b981', letterSpacing: '0.8px', textTransform: 'uppercase' }}>● Duration: {callDurationStr}</span>
              )}
            </div>
            <button type="button"
              className='btn btn-icon d-flex align-items-center justify-content-center rounded-3 shadow-sm'
              title="Tutup Panggilan"
              onClick={() => {
                if (currentSubId) {
                  setRealtimeDisposition(prev => ({
                    ...prev,
                    [currentSubId]: { status: 'completed', disposition: 'COMPLETED' }
                  }));
                }
                window.dispatchEvent(new CustomEvent('end-current-call'));
              }}
              style={{ width: 44, height: 44, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white', border: 'none', transition: 'all 0.2s' }}>
              <i className='bi bi-telephone-x-fill fs-4'></i>
            </button>
          </div>
        );
      })()}

      {/* CAMPAIGN SCRIPT INSIDE FORM */}
      {data?.script && (
        <div className="mb-5 rounded-4 overflow-hidden" style={{ border: '1px solid rgba(99,102,241,0.2)', boxShadow: '0 2px 8px rgba(99,102,241,0.06)' }}>
          <div className="accordion" id="form_script_accordion">
            <div className="accordion-item border-0">
              <h2 className="accordion-header" id="form_script_header">
                <button className="accordion-button collapsed fw-bold fs-7 py-3 px-5 border-0" type="button"
                  data-bs-toggle="collapse" data-bs-target="#form_script_body"
                  style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(59,130,246,0.05))', color: 'var(--bs-body-color)', borderRadius: '14px' }}>
                  <div className='d-flex align-items-center gap-2'>
                    <div className='rounded-2 d-flex align-items-center justify-content-center' style={{ width: 24, height: 24, background: 'linear-gradient(135deg,#6366f1,#3b82f6)' }}>
                      <i className="bi bi-journal-text text-white" style={{ fontSize: 11 }}></i>
                    </div>
                    Script Panduan Panggilan
                  </div>
                </button>
              </h2>
              <div id="form_script_body" className="accordion-collapse collapse" data-bs-parent="#form_script_accordion">
                <div className="accordion-body p-4 bg-body" style={{ maxHeight: '200px', overflowY: 'auto', borderTop: '1px solid var(--bs-border-color)' }}>
                  <div className='text-body fs-7 lh-lg' dangerouslySetInnerHTML={{ __html: data.script }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── GROUP TABS ── */}
      {Object.keys(groupedFields).length > 0 && (
        <div className='d-flex overflow-auto mb-4 gap-1 pb-1' style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {Object.keys(groupedFields).map((groupName, idx) => (
            <button
              type='button'
              key={idx}
              className='btn fw-bold flex-shrink-0'
              style={{
                borderRadius: '20px',
                padding: '5px 12px',
                fontSize: '11px',
                transition: 'all 0.18s',
                border: activeFormTab === groupName ? 'none' : '1px solid var(--bs-border-color)',
                background: activeFormTab === groupName
                  ? 'linear-gradient(135deg, #3b82f6, #6366f1)'
                  : 'transparent',
                color: activeFormTab === groupName ? '#fff' : 'var(--bs-gray-600)',
                boxShadow: activeFormTab === groupName ? '0 3px 10px rgba(99,102,241,0.35)' : 'none',
              }}
              onClick={() => setActiveFormTab(groupName)}
            >
              {groupName.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      )}

      {Object.entries(groupedFields).map(([groupName, fields], gIdx) => (
        <div key={gIdx} className={clsx('row g-3 g-md-4', activeFormTab !== groupName && 'd-none')}>
          {fields.map((field, idx) => (
            <div className='col-12 col-md-6' key={idx}>
              <label className='d-flex align-items-center gap-1 fw-bold mb-1 mb-md-2' style={{ fontSize: 11, color: 'var(--bs-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {field.fieldName}
                {(field.isRequired || field.canMf) && <span className='text-danger'>*</span>}
              </label>

              {(() => {
                const getFieldOptions = (f: any): string[] => {
                  const rawOpts = f.options || f.Options;
                  if (Array.isArray(rawOpts) && rawOpts.length > 0) return rawOpts;
                  if (typeof rawOpts === 'string' && rawOpts.trim()) {
                    try {
                      const parsed = JSON.parse(rawOpts);
                      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
                    } catch {
                      return rawOpts.split(',').map((s: string) => s.trim()).filter(Boolean);
                    }
                  }
                  const rawDef = f.defaultValue || f.DefaultValue;
                  if (typeof rawDef === 'string' && rawDef.trim() && ['Dropdown', 'Radio', 'Checkbox'].includes(f.fieldType)) {
                    return rawDef.split(',').map((s: string) => s.trim()).filter(Boolean);
                  }
                  return [];
                };

                const resolvedOptions = getFieldOptions(field);

                if (field.fieldType === 'Dropdown') {
                  const val = dynamicResults[field.fieldName || ''] || '';
                  return (
                    <div className='position-relative'>
                      <select
                        required={field.isRequired || field.canMf}
                        onInvalid={() => setActiveFormTab(groupName)}
                        className='form-select fw-semibold border-0'
                        style={{
                          height: 38, borderRadius: '10px', fontSize: 13,
                          background: val ? 'linear-gradient(135deg, rgba(59,130,246,0.06), var(--bs-body-bg))' : 'var(--bs-body-bg)',
                          border: val ? '1.5px solid rgba(59,130,246,0.35)' : '1.5px solid var(--bs-border-color)',
                          boxShadow: val ? '0 2px 8px rgba(59,130,246,0.10)' : '0 1px 4px rgba(0,0,0,0.04)',
                          color: val ? 'var(--bs-body-color)' : 'var(--bs-gray-500)',
                          paddingRight: 36, appearance: 'none',
                          transition: 'all 0.18s',
                        }}
                        value={val}
                        onChange={(e) => setDynamicResults(prev => ({ ...prev, [field.fieldName || '']: e.target.value }))}
                      >
                        <option value=''>— Pilih {field.fieldName} —</option>
                        {resolvedOptions.map((opt: string, i: number) => (
                          <option key={i} value={opt}>{opt}</option>
                        ))}
                      </select>
                      <i className='bi bi-chevron-down position-absolute top-50 translate-middle-y text-muted' style={{ right: 14, fontSize: 12, pointerEvents: 'none' }}></i>
                    </div>
                  );
                }

                if (field.fieldType === 'TextArea') {
                  return (
                    <textarea
                      required={field.isRequired || field.canMf}
                      onInvalid={() => setActiveFormTab(groupName)}
                      className='form-control fw-medium border-0'
                      style={{
                        borderRadius: '10px', fontSize: 13, resize: 'vertical',
                        background: 'var(--bs-body-bg)',
                        border: '1.5px solid var(--bs-border-color)',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                        transition: 'all 0.18s',
                      }}
                      rows={3}
                      value={dynamicResults[field.fieldName || ''] || ''}
                      onChange={(e) => setDynamicResults(prev => ({ ...prev, [field.fieldName || '']: e.target.value }))}
                    />
                  );
                }

                if (field.fieldType === 'Amount') {
                  return (
                    <div className="input-group input-group-sm">
                      <span className="input-group-text border-0" style={{ background: 'var(--bs-gray-200)', borderRadius: '10px 0 0 10px' }}>Rp</span>
                      <input
                        required={field.isRequired || field.canMf}
                        onInvalid={() => setActiveFormTab(groupName)}
                        type="number"
                        className='form-control form-control-solid fw-medium border-0'
                        style={{ height: 38, background: 'var(--bs-gray-100)', borderRadius: '0 10px 10px 0' }}
                        value={dynamicResults[field.fieldName || ''] || ''}
                        onChange={(e) => setDynamicResults(prev => ({ ...prev, [field.fieldName || '']: e.target.value }))}
                      />
                    </div>
                  );
                }

                if (field.fieldType === 'Percentage') {
                  return (
                    <div className="input-group input-group-sm">
                      <input
                        required={field.isRequired || field.canMf}
                        onInvalid={() => setActiveFormTab(groupName)}
                        type="number"
                        className='form-control form-control-solid fw-medium border-0'
                        style={{ height: 38, background: 'var(--bs-gray-100)', borderRadius: '10px 0 0 10px' }}
                        value={dynamicResults[field.fieldName || ''] || ''}
                        onChange={(e) => setDynamicResults(prev => ({ ...prev, [field.fieldName || '']: e.target.value }))}
                      />
                      <span className="input-group-text border-0" style={{ background: 'var(--bs-gray-200)', borderRadius: '0 10px 10px 0' }}>%</span>
                    </div>
                  );
                }

                if (field.fieldType === 'WYSIWYG') {
                  return (
                    <div style={{ background: 'var(--bs-gray-100)', borderRadius: '10px', padding: '8px' }}>
                      <textarea
                        required={field.isRequired || field.canMf}
                        className='form-control form-control-sm form-control-solid'
                        style={{ background: 'white', borderRadius: '8px' }}
                        value={dynamicResults[field.fieldName || ''] || ''}
                        onChange={(e) => setDynamicResults(prev => ({ ...prev, [field.fieldName || '']: e.target.value }))}
                      />
                    </div>
                  );
                }

                if (field.fieldType === 'Radio') {
                  return (
                    <div>
                      {resolvedOptions.map((opt: string, i: number) => (
                        <div className="form-check form-check-custom form-check-solid mb-2" key={i}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name={field.fieldName}
                            value={opt}
                            checked={dynamicResults[field.fieldName || ''] === opt}
                            onChange={(e) => setDynamicResults(prev => ({ ...prev, [field.fieldName || '']: e.target.value }))}
                            required={field.isRequired || field.canMf}
                            onInvalid={() => setActiveFormTab(groupName)}
                          />
                          <label className="form-check-label text-gray-700 fw-bold fs-8 fs-md-7">{opt}</label>
                        </div>
                      ))}
                    </div>
                  );
                }

                if (field.fieldType === 'Checkbox') {
                  return (
                    <div>
                      {resolvedOptions.map((opt: string, i: number) => {
                        const currentVals = (dynamicResults[field.fieldName || ''] || '').split(',').filter(Boolean);
                        return (
                          <div className="form-check form-check-custom form-check-solid mb-2" key={i}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              value={opt}
                              checked={currentVals.includes(opt)}
                              onChange={(e) => {
                                let newVals = [...currentVals];
                                if (e.target.checked) newVals.push(opt);
                                else newVals = newVals.filter((v: string) => v !== opt);
                                setDynamicResults(prev => ({ ...prev, [field.fieldName || '']: newVals.join(',') }));
                              }}
                            />
                            <label className="form-check-label text-gray-700 fw-bold fs-8 fs-md-7">{opt}</label>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                return (
                  <input
                    required={field.isRequired || field.canMf}
                    onInvalid={() => setActiveFormTab(groupName)}
                    type={
                      field.fieldType === 'Number' ? 'number' :
                        field.fieldType === 'Date' ? 'date' :
                          field.fieldType === 'Time' ? 'time' :
                            field.fieldType === 'DateTime' ? 'datetime-local' :
                              field.fieldType === 'Email' ? 'email' :
                                field.fieldType === 'Password' ? 'password' :
                                  field.fieldType === 'Phone' ? 'tel' : 'text'
                    }
                    className='form-control fw-semibold border-0'
                    style={{
                      height: 38, borderRadius: '10px', fontSize: 13,
                      background: 'var(--bs-body-bg)',
                      border: '1.5px solid var(--bs-border-color)',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                      transition: 'border-color 0.18s, box-shadow 0.18s',
                    }}
                    value={dynamicResults[field.fieldName || ''] || ''}
                    onChange={(e) => setDynamicResults(prev => ({ ...prev, [field.fieldName || '']: e.target.value }))}
                  />
                );
              })()}
            </div>
          ))}
        </div>
      ))}

      <div className='mt-4 pt-3' style={{ borderTop: '1px solid var(--bs-border-color)' }}>
        <label className='d-flex align-items-center gap-1 fw-bold mb-2' style={{ fontSize: 11, color: 'var(--bs-gray-500)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
          <i className='bi bi-telephone-fill' style={{ fontSize: 10 }}></i>
          Status Panggilan
        </label>
        <div className='d-flex align-items-center gap-3 px-3 px-md-4 py-2 py-md-3 rounded-3'
          style={{
            background: selectedLead?.call_count >= (data.retries ?? 3)
              ? 'rgba(239,68,68,0.06)'
              : 'rgba(16,185,129,0.06)',
            border: selectedLead?.call_count >= (data.retries ?? 3)
              ? '1.5px solid rgba(239,68,68,0.25)'
              : '1.5px solid rgba(16,185,129,0.25)',
          }}>
          <div className='d-flex align-items-center justify-content-center rounded-2 flex-shrink-0'
            style={{
              width: 32, height: 32,
              background: selectedLead?.call_count >= (data.retries ?? 3) ? 'rgba(239,68,68,0.12)' : 'rgba(16,185,129,0.12)',
              color: selectedLead?.call_count >= (data.retries ?? 3) ? '#ef4444' : '#10b981',
            }}>
            <i className={`bi ${selectedLead?.call_count >= (data.retries ?? 3) ? 'bi-x-circle-fill' : 'bi-check-circle-fill'}`} style={{ fontSize: 14 }}></i>
          </div>
          <div>
            <div className='fw-bold text-body fs-8 fs-md-7'>
              {selectedLead?.call_count >= (data.retries ?? 3)
                ? 'Batas Maksimal Terlewati'
                : `Panggilan ke-${(selectedLead?.call_count ?? 0) + 1} dari ${data.retries ?? 3}`}
            </div>
            <div className='text-muted' style={{ fontSize: 11 }}>
              {selectedLead?.call_count ?? 0} percobaan dilakukan
            </div>
          </div>
        </div>
      </div>
    </form>
  );

  const KANBAN_COLUMNS = [
    { id: 'Ready', title: 'Ready', gradient: ['#3b82f6', '#6366f1'], icon: 'bi-inbox-fill', dot: 'rgba(59,130,246,0.15)' },
    { id: 'Close', title: 'Close', gradient: ['#10b981', '#059669'], icon: 'bi-check-circle-fill', dot: 'rgba(16,185,129,0.15)' },
  ];

  const renderAgentWorkspaceKanban = () => {
    const dynamicCols = sortedFields.filter(f => f.canEdit && !['status_submission', 'call_disposition', 'call_count'].includes(f.fieldName || '')).slice(0, 2);
    const primaryCol = dynamicCols[0]?.fieldName || 'full_name';
    const secondaryCol = dynamicCols[1]?.fieldName || 'phone_number';

    return (
      <div className="d-flex flex-row w-100" style={{ minHeight: 'calc(100vh - 260px)' }}>
        <div className="d-flex flex-nowrap overflow-auto pb-6 gap-5 px-5 pt-5 flex-grow-1"
          style={{ background: 'var(--bs-body-bg)' }}>
          {KANBAN_COLUMNS.map(col => (
            <div key={col.id} className="d-flex flex-column flex-shrink-0 rounded-3"
              style={{
                width: '320px',
                background: col.dot,
                border: `1px solid ${col.gradient[0]}40`
              }}>
              {/* Column Header */}
              <div className="d-flex align-items-center justify-content-between px-4 py-3" style={{ borderBottom: `1px solid ${col.gradient[0]}20`, background: `linear-gradient(180deg, ${col.gradient[0]}10, transparent)` }}>
                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex align-items-center justify-content-center rounded-2"
                    style={{ width: 28, height: 28, background: `linear-gradient(135deg, ${col.gradient[0]}, ${col.gradient[1]})` }}>
                    <i className={`bi ${col.icon} text-white`} style={{ fontSize: 12 }}></i>
                  </div>
                  <span className="fw-boldest text-body fs-6" style={{ letterSpacing: '-0.3px' }}>{col.title}</span>
                </div>
                <span className="fw-boldest rounded-pill d-flex align-items-center justify-content-center text-white"
                  style={{
                    width: 26, height: 26, fontSize: '12px',
                    background: `linear-gradient(135deg, ${col.gradient[0]}, ${col.gradient[1]})`,
                    boxShadow: `0 2px 8px ${col.gradient[0]}40`,
                  }}>
                  {groupedData[col.id].length}
                </span>
              </div>

              {/* Column Body */}
              <div className="flex-grow-1 p-3 overflow-auto custom-scrollbar"
                style={{
                  maxHeight: 'calc(100vh - 320px)',
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const leadId = e.dataTransfer.getData('leadId');
                  const lead = displayData.find(l => String(l.id) === leadId);
                  if (lead && lead.status_submission !== col.id) {
                    const updatedLead = { ...lead, status_submission: col.id };
                    const stringifiedLead: Record<string, string> = {};
                    Object.keys(updatedLead).forEach(key => {
                      if (updatedLead[key] !== null && updatedLead[key] !== undefined) {
                        stringifiedLead[key] = String(updatedLead[key]);
                      }
                    });

                    toast.promise(
                      saveOutboundCall({
                        campaignId: data.id as string,
                        submissionId: lead.id,
                        webhookResults: stringifiedLead,
                        notes: ''
                      }).then(() => fetchLeads(currentPage)),
                      {
                        pending: `Memindahkan ke ${col.title}...`,
                        success: 'Berhasil dipindahkan',
                        error: 'Gagal memindahkan'
                      }
                    );
                  }
                }}
              >
                {groupedData[col.id].length === 0 && (
                  <div className="d-flex flex-column align-items-center justify-content-center py-10 opacity-40">
                    <i className={`bi ${col.icon} fs-3x mb-3`} style={{ color: col.gradient[0] }}></i>
                    <span className="fs-8 fw-semibold text-muted">Kosong</span>
                  </div>
                )}
                {groupedData[col.id].sort((a, b) => {
                  const rtA = realtimeDisposition[String(a.id)]?.disposition;
                  const rtB = realtimeDisposition[String(b.id)]?.disposition;
                  const isActiveA = a.status_submission === 'Calling' || rtA === 'Calling...' || rtA === 'Ringing' || rtA === 'Connected';
                  const isActiveB = b.status_submission === 'Calling' || rtB === 'Calling...' || rtB === 'Ringing' || rtB === 'Connected';
                  if (isActiveA && !isActiveB) return -1;
                  if (!isActiveA && isActiveB) return 1;
                  return 0;
                }).map(row => {
                  const rtData = realtimeDisposition[String(row.id)];
                  const dispositionRt = rtData?.disposition ?? row.call_disposition;
                  const isSelected = selectedLead?.id === row.id;
                  const isActive = row.status_submission === 'Calling' || dispositionRt === 'Calling...' || dispositionRt === 'Ringing' || dispositionRt === 'Connected';
                  const primaryVal = row[primaryCol] || row.full_name || 'No Name';
                  const secondaryVal = row[secondaryCol] || row.phone_number || '-';
                  const initials = String(primaryVal).split(' ').map((w: string) => w[0]).slice(0, 2).join('');

                  return (
                    <div key={row.id}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('leadId', String(row.id)); }}
                      className="mb-3 cursor-pointer"
                      style={{ borderRadius: '14px', transition: 'all 0.2s' }}
                      onClick={() => {
                        setSelectedLead({ ...row });
                      }}
                    >
                      <div className="hover-elevate-up" style={{
                        borderRadius: '14px',
                        background: isSelected ? 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,1))' : 'var(--bs-body-bg)',
                        border: isSelected ? `2px solid ${col.gradient[0]}` : `1px solid var(--bs-border-color)`,
                        boxShadow: isSelected
                          ? `0 6px 20px ${col.gradient[0]}35`
                          : '0 4px 12px rgba(0,0,0,0.06)',
                        transition: 'all 0.2s',
                      }}>
                        <div className="p-4">
                          <div className="d-flex align-items-center gap-3 mb-3">
                            {/* Avatar */}
                            <div className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle fw-boldest text-white position-relative"
                              style={{
                                width: 36, height: 36, fontSize: '13px',
                                background: isActive
                                  ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
                                  : `linear-gradient(135deg, ${col.gradient[0]}, ${col.gradient[1]})`,
                                boxShadow: `0 3px 10px ${col.gradient[0]}40`,
                              }}>
                              {initials}
                              {isActive && (
                                <span className="position-absolute bg-danger border border-2 border-white rounded-circle animate__animated animate__pulse animate__infinite"
                                  style={{ width: 10, height: 10, top: -2, right: -2 }}></span>
                              )}
                            </div>
                            <div className="flex-grow-1 min-w-0">
                              <div className="fw-bolder text-body text-truncate" style={{ fontSize: '14px' }}>
                                {primaryVal}
                              </div>
                              <div className="text-muted text-truncate" style={{ fontSize: '11px', marginTop: '2px' }}>
                                {dynamicCols[0]?.fieldName || 'Name'}
                              </div>
                            </div>
                            {isActive && (
                              <span className="badge text-white fw-bold animate__animated animate__pulse animate__infinite"
                                style={{ fontSize: '10px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', borderRadius: '6px', padding: '4px 8px', boxShadow: '0 2px 8px rgba(239,68,68,0.4)' }}>LIVE</span>
                            )}
                          </div>

                          {dynamicCols.length > 1 && (
                            <div className="d-flex align-items-center gap-2 mb-3 px-3 py-2 rounded-3"
                              style={{ background: `${col.gradient[0]}08`, fontSize: '12px' }}>
                              <i className={clsx('bi bi-info-circle-fill', isActive ? 'animate__animated animate__tada animate__infinite' : '')}
                                style={{ color: isActive ? '#ef4444' : col.gradient[0], fontSize: '12px' }}></i>
                              <div className="d-flex flex-column">
                                <span style={{ fontSize: '9px', textTransform: 'uppercase', color: col.gradient[0], opacity: 0.8, fontWeight: 700 }}>{dynamicCols[1]?.fieldName || 'Info'}</span>
                                <span className="text-muted fw-bold" style={{ fontSize: '12px', color: 'var(--bs-gray-700)' }}>{secondaryVal}</span>
                              </div>
                            </div>
                          )}

                          <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted" style={{ fontSize: '11px' }}>
                              <i className="bi bi-arrow-repeat me-1 opacity-50"></i>
                              {row.call_count}/{data.retries ?? 3}
                            </span>
                            {(data.dialMode === 'Auto' || data.dialMode === 'Predictive') && dispositionRt && (
                              <span className="fw-semibold rounded-pill px-2"
                                style={{ fontSize: '10px', background: `${col.gradient[0]}15`, color: col.gradient[0] }}>
                                {dispositionRt}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* SIDE FORM FOR KANBAN */}
        {selectedLead && (
          <div className="border-start border-gray-200 shadow-sm" style={{ width: 500, minWidth: 500, background: '#f8fafc', overflowY: 'auto' }}>
            {renderCallInterface()}
          </div>
        )}
      </div>
    );
  }

  const renderAgentWorkspaceTable = () => {
    const dynamicCols = sortedFields.filter(f => f.canEdit && !['status_submission', 'call_disposition', 'call_count'].includes(f.fieldName || '')).slice(0, 3);
    const tableHeaders = ['#', ...dynamicCols.map(c => c.fieldName || ''), 'Status', 'Panggilan', 'Disposisi', 'Aksi'];

    const statusConfig: Record<string, { color: string, bg: string, gradient: string[] }> = {
      'Calling': { color: '#d97706', bg: 'rgba(245,158,11,0.12)', gradient: ['#f59e0b', '#d97706'] },
      'Follow Up': { color: '#7c3aed', bg: 'rgba(139,92,246,0.12)', gradient: ['#8b5cf6', '#7c3aed'] },
      'Close': { color: '#059669', bg: 'rgba(16,185,129,0.12)', gradient: ['#10b981', '#059669'] },
      'Ready': { color: '#2563eb', bg: 'rgba(59,130,246,0.12)', gradient: ['#3b82f6', '#2563eb'] },
    };
    return (
      <div style={{ overflowX: 'auto', background: 'var(--bs-body-bg)', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <table className="table table-hover table-row-dashed align-middle gs-0 gy-4 mb-0" style={{ minWidth: 900 }}>
          <thead>
            <tr className="border-bottom border-gray-200">
              {tableHeaders.map((h, i) => (
                <th key={i} className="fw-boldest text-muted py-4 px-5" style={{ fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', background: 'var(--bs-gray-100)', whiteSpace: 'nowrap', borderTopLeftRadius: i === 0 ? '12px' : 0, borderTopRightRadius: i === tableHeaders.length - 1 ? '12px' : 0 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-12">
                  <div className="d-flex flex-column align-items-center gap-3 opacity-50">
                    <i className="bi bi-table text-muted" style={{ fontSize: 48 }}></i>
                    <span className="text-muted fw-semibold fs-6">Tidak ada data leads</span>
                  </div>
                </td>
              </tr>
            )}
            {displayData.map((row, idx) => {
              const rtData = realtimeDisposition[String(row.id)];
              const dispositionRt = rtData?.disposition ?? row.call_disposition;
              const isSelected = selectedLead?.id === row.id;
              const isActive = row.status_submission === 'Calling' || dispositionRt === 'Calling...' || dispositionRt === 'Ringing' || dispositionRt === 'Connected';
              const statusKey = row.status_submission || 'Ready';
              const sc = statusConfig[statusKey] || statusConfig['Ready'];
              const initials = (row.full_name || 'NN').split(' ').map((w: string) => w[0]).slice(0, 2).join('');
              const globalIdx = (currentPage - 1) * 20 + idx + 1;

              return (
                <tr key={row.id}
                  onClick={() => { setSelectedLead({ ...row }); }}
                  className="cursor-pointer transition-all"
                  style={{
                    background: isSelected ? 'linear-gradient(135deg, rgba(59,130,246,0.06) 0%, var(--bs-body-bg) 100%)' : 'var(--bs-body-bg)',
                    borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  {/* Index */}
                  <td className="py-3 px-5" style={{ width: 48, color: 'var(--bs-gray-400)', fontSize: 13, fontWeight: 600 }}>{globalIdx}</td>
                  {/* Dynamic Columns */}
                  {dynamicCols.map((col, cIdx) => {
                    const val = row[col.fieldName || ''];
                    if (cIdx === 0) {
                      return (
                        <td key={cIdx} className="py-3 px-5">
                          <div className="d-flex align-items-center gap-3">
                            <div className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle fw-boldest text-white position-relative"
                              style={{
                                width: 38, height: 38, fontSize: '13px',
                                background: isActive
                                  ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
                                  : `linear-gradient(135deg, ${sc.gradient[0]}, ${sc.gradient[1]})`,
                                boxShadow: `0 3px 10px ${sc.color}40`,
                              }}>
                              {String(val || row.full_name || 'NN').split(' ').map((w: string) => w[0]).slice(0, 2).join('')}
                              {isActive && (
                                <span className="position-absolute bg-danger border border-2 border-white rounded-circle animate__animated animate__pulse animate__infinite"
                                  style={{ width: 10, height: 10, top: -2, right: -2 }}></span>
                              )}
                            </div>
                            <div>
                              <div className="fw-boldest text-body text-hover-primary" style={{ fontSize: '14px', letterSpacing: '-0.2px' }}>
                                {val || row.full_name || 'No Data'}
                              </div>
                            </div>
                          </div>
                        </td>
                      );
                    }
                    return (
                      <td key={cIdx} className="py-3 px-5">
                        <span className="text-gray-700 fw-semibold" style={{ fontSize: '13px' }}>
                          {val || '-'}
                        </span>
                      </td>
                    );
                  })}
                  {/* Status */}
                  <td className="py-3 px-5">
                    <span className="badge rounded-pill fw-bold px-3 py-1"
                      style={{ background: sc.bg, color: sc.color, fontSize: '11px' }}>
                      {isActive ? '● LIVE' : statusKey}
                    </span>
                  </td>
                  {/* Calls */}
                  <td className="py-3 px-5">
                    <div className="d-flex align-items-center gap-2">
                      <div className="rounded-pill overflow-hidden" style={{ width: 60, height: 6, background: 'var(--bs-light)' }}>
                        <div className="h-100 rounded-pill" style={{
                          width: `${Math.min(100, (row.call_count / (data.retries ?? 3)) * 100)}%`,
                          background: row.call_count >= (data.retries ?? 3) ? '#10b981' : '#3b82f6',
                        }}></div>
                      </div>
                      <span className="text-muted" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{row.call_count}/{data.retries ?? 3}</span>
                    </div>
                  </td>
                  {/* Disposition */}
                  <td className="py-3 px-5">
                    <span className="text-muted" style={{ fontSize: '12px' }}>{dispositionRt || '-'}</span>
                  </td>
                  {/* Action */}
                  <td className="py-3 px-5">
                    <button className="btn btn-sm fw-bold d-flex align-items-center gap-1"
                      style={{
                        background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                        color: 'white', border: 'none', borderRadius: '8px',
                        padding: '5px 14px', fontSize: '12px',
                        boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openCallDrawer({ ...row }, callFieldName);
                      }}>
                      <i className="bi bi-telephone-fill" style={{ fontSize: 10 }}></i>
                      Buka
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* Table Pagination */}
        {totalData > 0 && (
          <div className="d-flex align-items-center justify-content-between px-5 py-4" style={{ borderTop: '1px solid var(--bs-border-color)' }}>
            <span className="text-muted fs-8 fw-medium">Menampilkan {displayData.length} dari {totalData} leads</span>
            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-sm fw-bold d-flex align-items-center gap-1"
                style={{
                  borderRadius: '8px', padding: '6px 14px', fontSize: '12px',
                  background: currentPage === 1 ? 'var(--bs-light)' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
                  color: currentPage === 1 ? 'var(--bs-gray-500)' : 'white', border: 'none',
                  boxShadow: currentPage === 1 ? 'none' : '0 2px 8px rgba(99,102,241,0.3)',
                }}
                onClick={() => fetchLeads(currentPage - 1)} disabled={currentPage === 1}>
                <i className="bi bi-chevron-left" style={{ fontSize: 11 }}></i> Prev
              </button>
              <span className="px-3 py-1 rounded-pill fw-bold" style={{ fontSize: '12px', background: 'var(--bs-light)', color: 'var(--bs-body-color)' }}>
                {currentPage} / {totalPages}
              </span>
              <button className="btn btn-sm fw-bold d-flex align-items-center gap-1"
                style={{
                  borderRadius: '8px', padding: '6px 14px', fontSize: '12px',
                  background: currentPage === totalPages ? 'var(--bs-light)' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
                  color: currentPage === totalPages ? 'var(--bs-gray-500)' : 'white', border: 'none',
                  boxShadow: currentPage === totalPages ? 'none' : '0 2px 8px rgba(99,102,241,0.3)',
                }}
                onClick={() => fetchLeads(currentPage + 1)} disabled={currentPage === totalPages}>
                Next <i className="bi bi-chevron-right" style={{ fontSize: 11 }}></i>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const renderAgentWorkspaceList = () => {
    const statusConfig: Record<string, { color: string, bg: string, label: string }> = {
      'Calling': { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'Calling' },
      'Follow Up': { color: '#6366f1', bg: 'rgba(99,102,241,0.12)', label: 'Follow Up' },
      'Close': { color: '#10b981', bg: 'rgba(16,185,129,0.12)', label: 'Close' },
      'Ready': { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', label: 'Ready' },
    };

    return (
      <div className="row g-0" style={{ minHeight: '500px', height: 'calc(100vh - 280px)' }}>
        {/* ── LEFT PANE ── */}
        <div className={clsx(
          "col-12 col-xl-4 d-flex flex-column h-100 position-relative",
          selectedLead && "d-none d-xl-flex"
        )}
          style={{
            borderRight: '1px solid var(--bs-border-color)',
            background: 'var(--bs-body-bg)',
          }}
        >
          {/* Pane Header */}
          <div className="px-4 px-md-5 py-3 py-md-4 d-flex align-items-center justify-content-between flex-wrap gap-2"
            style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center justify-content-center rounded-2"
                style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)' }}>
                <i className="bi bi-people-fill text-white fs-7"></i>
              </div>
              <div>
                <div className="fw-bolder text-body fs-6" style={{ letterSpacing: '-0.3px' }}>Antrean Leads</div>
                <div className="fs-8 text-muted">{totalData} nasabah</div>
              </div>
            </div>

            {/* Lead Queue Progress Indicator */}
            {(() => {
              const closedCount = groupedData['Close']?.length || 0;
              const totalCount = displayData.length || totalData || 1;
              const percent = Math.min(100, Math.round((closedCount / totalCount) * 100));

              return (
                <div className="d-flex flex-column align-items-end gap-1">
                  <span className="badge badge-light-primary fw-bolder fs-9 px-2.5 py-1 rounded-pill">
                    {closedCount}/{totalCount} ({percent}% Selesai)
                  </span>
                  <div className="rounded-pill overflow-hidden" style={{ width: 90, height: 5, background: 'var(--bs-gray-200)' }}>
                    <div style={{ width: `${percent}%`, height: '100%', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: 'inherit', transition: 'width 0.3s ease' }}></div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Leads List */}
          <div className="flex-grow-1 overflow-auto px-3 py-3 custom-scrollbar" style={{ maxHeight: 'calc(100vh - 340px)' }}>
            {displayData.length === 0 && (
              <div className="text-center py-14">
                <div className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-4"
                  style={{ width: 64, height: 64, background: 'var(--bs-light)' }}>
                  <i className="bi bi-inbox text-muted fs-2"></i>
                </div>
                <p className="text-muted fs-7 fw-semibold m-0">Belum ada leads.</p>
              </div>
            )}
            {(() => {
              const sortedSidebarData = [...displayData].sort((a, b) => {
                const rtA = realtimeDisposition[String(a.id)]?.disposition;
                const rtB = realtimeDisposition[String(b.id)]?.disposition;
                const isActiveA = a.status_submission === 'Calling' || rtA === 'Calling...' || rtA === 'Ringing' || rtA === 'Connected';
                const isActiveB = b.status_submission === 'Calling' || rtB === 'Calling...' || rtB === 'Ringing' || rtB === 'Connected';
                if (isActiveA && !isActiveB) return -1;
                if (!isActiveA && isActiveB) return 1;
                return 0;
              });

              return sortedSidebarData.map((row) => {
                const rtData = realtimeDisposition[String(row.id)];
                const dispositionRt = rtData?.disposition ?? row.call_disposition;
                const isSelected = selectedLead?.id === row.id;
                const isActive = row.status_submission === 'Calling' || dispositionRt === 'Calling...' || dispositionRt === 'Ringing' || dispositionRt === 'Connected';
                const statusKey = row.status_submission || 'Ready';
                const sc = statusConfig[statusKey] || statusConfig['Ready'];
                const dynamicPrimaryCol = sortedFields.filter(f => f.canEdit && !['status_submission', 'call_disposition', 'call_count'].includes(f.fieldName || ''))[0]?.fieldName || 'full_name';
                const displayName = row[dynamicPrimaryCol] || row.full_name || 'No Name';
                const initials = (displayName === 'No Name' ? 'NN' : displayName).split(' ').map((w: string) => w[0]).slice(0, 2).join('');

                return (
                  <div key={row.id}
                    className="mb-2 cursor-pointer transition-all"
                    style={{ borderRadius: '14px', overflow: 'hidden' }}
                    onClick={() => {
                      setSelectedLead({ ...row });
                    }}
                  >
                    <div style={{
                      borderRadius: '14px',
                      border: isSelected
                        ? '1.5px solid #3b82f6'
                        : isActive
                          ? '1.5px solid #f59e0b'
                          : '1.5px solid var(--bs-border-color)',
                      background: isSelected
                        ? 'linear-gradient(135deg, rgba(59,130,246,0.06) 0%, var(--bs-body-bg) 100%)'
                        : 'var(--bs-body-bg)',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                      transition: 'all 0.2s ease',
                    }}>
                      <div className="p-3">
                        <div className="d-flex align-items-center gap-3">
                          {/* Avatar */}
                          <div className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle fw-boldest text-white fs-7 position-relative"
                            style={{
                              width: 38, height: 38, flexShrink: 0,
                              background: isActive
                                ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
                                : `linear-gradient(135deg, ${sc.color}, ${sc.color}cc)`,
                              boxShadow: `0 2px 8px ${sc.color}40`,
                            }}>
                            {initials}
                            {isActive && (
                              <span className="position-absolute top-0 end-0 translate-middle p-1 bg-danger border border-2 border-white rounded-circle animate__animated animate__pulse animate__infinite"
                                style={{ width: 10, height: 10 }}></span>
                            )}
                          </div>
                          {/* Info */}
                          <div className="flex-grow-1 min-w-0">
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <span className={clsx("fw-bolder fs-7 text-truncate", isSelected ? "text-primary" : "text-body")} style={{ maxWidth: '68%' }}>
                                {displayName}
                              </span>
                              <span className="badge rounded-pill fw-bold fs-9 px-2 py-1 flex-shrink-0"
                                style={{ background: sc.bg, color: sc.color, fontSize: '10px' }}>
                                {isActive ? '● LIVE' : statusKey.substring(0, 5)}
                              </span>
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <span className="fs-8 text-muted fw-medium d-flex align-items-center gap-1">
                                <i className={clsx("bi bi-telephone-fill fs-9", isActive ? "text-danger animate__animated animate__tada animate__infinite" : "text-muted")}></i>
                                {row.phone_number || '-'}
                              </span>
                              <span className="fs-9 text-muted">
                                <i className="bi bi-arrow-repeat me-1 opacity-50"></i>
                                {row.call_count}/{data.retries ?? 3}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          {/* Pagination */}
          {totalData > 0 && (
            <div className="px-4 py-3 d-flex justify-content-between align-items-center"
              style={{ borderTop: '1px solid var(--bs-border-color)', background: 'var(--bs-body-bg)' }}>
              <button
                className="btn btn-sm btn-icon d-flex align-items-center justify-content-center"
                style={{
                  width: 32, height: 32, borderRadius: '8px',
                  background: currentPage === 1 ? 'var(--bs-light)' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
                  color: currentPage === 1 ? 'var(--bs-gray-500)' : 'white',
                  border: 'none', boxShadow: currentPage === 1 ? 'none' : '0 2px 8px rgba(99,102,241,0.35)',
                  transition: 'all 0.2s',
                }}
                onClick={() => fetchLeads(currentPage - 1)} disabled={currentPage === 1}>
                <i className="bi bi-chevron-left fs-8"></i>
              </button>
              <span className="fs-8 fw-semibold text-muted">
                {currentPage} <span className="text-body-secondary">/</span> {totalPages}
              </span>
              <button
                className="btn btn-sm btn-icon d-flex align-items-center justify-content-center"
                style={{
                  width: 32, height: 32, borderRadius: '8px',
                  background: currentPage === totalPages ? 'var(--bs-light)' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
                  color: currentPage === totalPages ? 'var(--bs-gray-500)' : 'white',
                  border: 'none', boxShadow: currentPage === totalPages ? 'none' : '0 2px 8px rgba(99,102,241,0.35)',
                  transition: 'all 0.2s',
                }}
                onClick={() => fetchLeads(currentPage + 1)} disabled={currentPage === totalPages}>
                <i className="bi bi-chevron-right fs-8"></i>
              </button>
            </div>
          )}
        </div>

        {/* ── RIGHT PANE ── */}
        <div className={clsx(
          "col-12 col-xl-8 d-flex flex-column h-100 position-relative",
          !selectedLead && "d-none d-xl-flex"
        )}
          style={{ background: 'var(--bs-body-bg)' }}>
          {renderCallInterface()}
        </div>
      </div>
    );
  }

  const renderCallInterface = () => {
    const statusConfig: Record<string, { color: string, bg: string, label: string }> = {
      'Calling': { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'Calling' },
      'Follow Up': { color: '#6366f1', bg: 'rgba(99,102,241,0.12)', label: 'Follow Up' },
      'Close': { color: '#10b981', bg: 'rgba(16,185,129,0.12)', label: 'Close' },
      'Ready': { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', label: 'Ready' },
    };

    return selectedLead ? (
      <div className="d-flex flex-column h-100 w-100">
        {/* Right Pane Header */}
        <div className="px-4 px-md-6 py-3 py-md-4 d-flex flex-wrap justify-content-between align-items-center gap-3"
          style={{
            borderBottom: '1px solid var(--bs-border-color)',
            background: 'linear-gradient(135deg, rgba(59,130,246,0.05) 0%, var(--bs-body-bg) 100%)',
          }}>
          <div className="d-flex align-items-center gap-3 gap-md-4">
            <button
              type="button"
              onClick={() => setSelectedLead(null)}
              className="btn btn-sm btn-icon btn-light d-xl-none"
              title="Kembali ke Daftar"
            >
              <i className="bi bi-arrow-left fs-4 text-primary"></i>
            </button>
            <div className="d-flex align-items-center justify-content-center rounded-circle fw-boldest text-white fs-4 fs-md-3"
              style={{
                width: 44, height: 44,
                background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                boxShadow: '0 4px 16px rgba(99,102,241,0.4)',
                flexShrink: 0,
              }}>
              {(() => {
                const dynamicPrimaryCol = sortedFields.filter(f => f.canEdit && !['status_submission', 'call_disposition', 'call_count'].includes(f.fieldName || ''))[0]?.fieldName || 'full_name';
                const slName = selectedLead[dynamicPrimaryCol] || selectedLead.full_name || 'No Name';
                return slName !== 'No Name' ? slName.charAt(0).toUpperCase() : 'C';
              })()}
            </div>
            <div>
              <div className="fw-bolder text-body fs-5 fs-md-4" style={{ letterSpacing: '-0.5px' }}>
                {(() => {
                  const dynamicPrimaryCol = sortedFields.filter(f => f.canEdit && !['status_submission', 'call_disposition', 'call_count'].includes(f.fieldName || ''))[0]?.fieldName || 'full_name';
                  return selectedLead[dynamicPrimaryCol] || selectedLead.full_name || 'No Name';
                })()}
              </div>
              <div className="d-flex align-items-center gap-2 gap-md-3 mt-1 flex-wrap">
                <span className="text-muted fs-8 fs-md-7 fw-medium d-flex align-items-center gap-1">
                  <i className="bi bi-telephone text-primary"></i>
                  {selectedLead.phone_number}
                </span>
                <span className="badge rounded-pill px-2 py-1 fs-9 fw-bold"
                  style={{
                    background: (statusConfig[selectedLead.status_submission] || statusConfig['Ready']).bg,
                    color: (statusConfig[selectedLead.status_submission] || statusConfig['Ready']).color
                  }}>
                  {selectedLead.status_submission || 'Ready'}
                </span>
              </div>
            </div>
          </div>
          {(() => {
            const currentSubId = String(selectedLead.id);
            const currentDisp = realtimeDisposition[currentSubId]?.disposition;
            const isCalling = currentDisp === 'CALLING' || currentDisp === 'ANSWERED';

            if (selectedLead.call_count >= (data.retries ?? 3) || selectedLead.status_submission === 'Close') return null;

            return (
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn fw-bold d-flex align-items-center gap-2 shadow-sm fs-8 fs-md-7"
                  disabled={isCalling}
                  style={{
                    background: isCalling ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    border: 'none',
                    padding: '8px 16px',
                    boxShadow: isCalling ? '0 4px 16px rgba(245,158,11,0.4)' : '0 4px 16px rgba(16,185,129,0.4)',
                    transition: 'all 0.2s',
                    opacity: isCalling ? 0.9 : 1,
                    cursor: isCalling ? 'not-allowed' : 'pointer'
                  }}
                  onClick={() => {
                    const fName = callFieldName;
                    if (fName) {
                      openCallDrawer(selectedLead, fName);
                      setIsDrawerOpen(false);
                    } else {
                      toast.error("Tidak ada field telepon untuk melakukan panggilan");
                    }
                  }}>
                  <span className="d-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25"
                    style={{ width: 20, height: 20 }}>
                    {isCalling ? (
                      <i className="bi bi-telephone-outbound-fill" style={{ fontSize: 10 }}></i>
                    ) : (
                      <i className="bi bi-telephone-fill" style={{ fontSize: 10 }}></i>
                    )}
                  </span>
                  {isCalling ? (currentDisp === 'ANSWERED' ? callDurationStr : 'Berdering...') : 'Panggil'}
                </button>

                {isCalling && (
                  <button
                    className="btn btn-icon shadow-sm"
                    title="Tutup Panggilan"
                    style={{
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      borderRadius: '12px',
                      border: 'none',
                      width: 38, height: 38,
                      boxShadow: '0 4px 16px rgba(239,68,68,0.4)',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => {
                      if (currentSubId) {
                        setRealtimeDisposition(prev => ({
                          ...prev,
                          [currentSubId]: { status: 'completed', disposition: 'COMPLETED' }
                        }));
                      }
                      window.dispatchEvent(new CustomEvent('end-current-call'));
                    }}>
                    <i className="bi bi-telephone-x-fill fs-6"></i>
                  </button>
                )}
              </div>
            );
          })()}
        </div>

        {/* Body Right Pane */}
        <div className="flex-grow-1 overflow-auto p-4 p-md-8 p-lg-10" style={{ maxHeight: 'calc(100vh - 350px)' }}>
          {renderFormBody()}
        </div>

        {/* Footer Right Pane */}
        <div className="px-4 px-md-8 py-3 py-md-4 d-flex align-items-center justify-content-between gap-2"
          style={{
            borderTop: '1px solid var(--bs-border-color)',
            background: 'var(--bs-body-bg)',
          }}>
          <button type="button"
            className="btn btn-sm btn-md-base fw-bold d-flex align-items-center gap-1 gap-md-2 fs-8 fs-md-7 px-3 px-md-5 py-2"
            style={{
              background: 'var(--bs-light)',
              color: 'var(--bs-gray-600)',
              borderRadius: '10px',
              border: '1.5px solid var(--bs-border-color)',
              transition: 'all 0.2s',
            }}
            onClick={() => setSelectedLead(null)}>
            <i className="bi bi-x-lg fs-8"></i>
            Tutup
          </button>
          <button type="submit" form="kt_call_result_form" id="btn-save-call-result"
            className="btn btn-sm btn-md-base fw-bold d-flex align-items-center gap-1 gap-md-2 fs-8 fs-md-7 px-4 px-md-7 py-2"
            style={{
              background: loading ? 'var(--bs-light)' : 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
              color: loading ? 'var(--bs-gray-500)' : 'white',
              borderRadius: '10px',
              border: 'none',
              boxShadow: loading ? 'none' : '0 4px 16px rgba(99,102,241,0.4)',
              transition: 'all 0.2s',
            }}
            disabled={loading}>
            {!loading ? (
              <>
                <i className="bi bi-check-circle-fill fs-8"></i>
                Simpan Hasil
                <span className="badge bg-white bg-opacity-25 text-white ms-1 py-0.5 px-1.5 fs-10 d-none d-sm-inline-block" style={{ borderRadius: '4px' }}>Ctrl+↵</span>
              </>
            ) : (
              <span className="spinner-border spinner-border-sm"></span>
            )}
          </button>
        </div>
      </div>
    ) : (
      /* ── PREMIUM EMPTY STATE ── */
      <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center px-10"
        style={{
          background: 'radial-gradient(ellipse at 60% 40%, rgba(59,130,246,0.06) 0%, transparent 65%), radial-gradient(ellipse at 30% 80%, rgba(99,102,241,0.04) 0%, transparent 55%)',
        }}>

        {/* Decorative grid pattern */}
        <div className="position-absolute top-0 start-0 w-100 h-100 opacity-25" style={{
          backgroundImage: 'radial-gradient(circle, rgba(99,102,241,0.15) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          pointerEvents: 'none',
        }}></div>

        {/* Main icon cluster */}
        <div className="position-relative mb-8 z-1">
          {/* Outer ring */}
          <div className="rounded-circle position-absolute top-50 start-50 translate-middle animate__animated animate__pulse animate__infinite"
            style={{
              width: 140, height: 140,
              background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
              border: '1px dashed rgba(99,102,241,0.2)',
            }}></div>
          {/* Main avatar */}
          <div className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: 88, height: 88,
              background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
              boxShadow: '0 8px 32px rgba(99,102,241,0.35)',
            }}>
            <i className="bi bi-person-check-fill text-white" style={{ fontSize: 38 }}></i>
          </div>
          {/* Floating badges */}
          <span className="position-absolute top-0 end-0 d-flex align-items-center gap-1 text-white fw-bold rounded-pill px-2 py-1 shadow-lg"
            style={{ fontSize: '10px', background: 'linear-gradient(135deg, #10b981, #059669)', top: '-8px !important', right: '-16px', transform: 'none' }}>
            <i className="bi bi-check2 fs-9"></i> Simpan
          </span>
          <span className="position-absolute bottom-0 start-0 d-flex align-items-center gap-1 text-white fw-bold rounded-pill px-2 py-1 shadow-lg"
            style={{ fontSize: '10px', background: 'linear-gradient(135deg, #f59e0b, #ef4444)', bottom: '-8px !important', left: '-16px', transform: 'none' }}>
            <i className="bi bi-telephone fs-9"></i> Telepon
          </span>
        </div>

        <h3 className="fw-bolder mb-3 z-1" style={{ color: 'var(--bs-body-color)', letterSpacing: '-0.5px', fontSize: '1.3rem' }}>
          Pilih Lead dari Daftar
        </h3>
        <p className="text-muted fs-7 mb-8 z-1" style={{ maxWidth: 300, lineHeight: 1.8 }}>
          Klik salah satu nasabah di kiri untuk membuka rincian data dan mulai melakukan panggilan.
        </p>

        {/* Feature pills */}
        <div className="d-flex flex-wrap justify-content-center gap-3 z-1">
          {[
            { icon: 'bi-telephone-fill', label: 'Klik untuk Telepon', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
            { icon: 'bi-journal-text', label: 'Isi Call Result', color: '#6366f1', bg: 'rgba(99,102,241,0.1)' },
            { icon: 'bi-graph-up', label: 'Pantau Progress', color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
          ].map((item, i) => (
            <div key={i} className="d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold fs-8"
              style={{ background: item.bg, color: item.color, border: `1px solid ${item.color}30` }}>
              <i className={`bi ${item.icon} fs-8`}></i>
              {item.label}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAgentWorkspace = () => {
    const modeOptions = [
      { id: 'list', icon: 'bi-layout-sidebar-reverse', label: 'List' },
      { id: 'table', icon: 'bi-table', label: 'Table' },
      { id: 'kanban', icon: 'bi-kanban', label: 'Kanban' },
    ] as const;

    const modeLabels: Record<string, { icon: string; label: string }> = {
      list: { icon: 'bi-layout-sidebar-reverse', label: 'List View' },
      table: { icon: 'bi-table', label: 'Table View' },
      kanban: { icon: 'bi-kanban', label: 'Kanban Board' },
    };

    const current = modeLabels[agentViewMode];

    return (
      <div className="d-flex flex-column">
        {/* ── TOOLBAR ── */}
        <div className="d-flex justify-content-between align-items-center px-6 py-3"
          style={{ borderBottom: '1px solid var(--bs-border-color)', background: 'var(--bs-body-bg)' }}>
          {/* Left: Active view label */}
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center justify-content-center rounded-2"
              style={{ width: 32, height: 32, background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
              <i className={`bi ${current.icon} text-white fs-7`}></i>
            </div>
            <div>
              <div className="fw-boldest text-body fs-6" style={{ letterSpacing: '-0.3px' }}>{current.label}</div>
              <div className="fs-9 text-muted">{totalData} leads total</div>
            </div>
          </div>

          {/* Right: Mode Switcher */}
          <div className="d-flex align-items-center gap-1 p-1 rounded-3"
            style={{ background: 'var(--bs-light)', border: '1px solid var(--bs-border-color)' }}>
            {modeOptions.map(opt => (
              <button key={opt.id}
                onClick={() => {
                  setAgentViewMode(opt.id);
                  setIsDrawerOpen(false);
                }}
                title={opt.label}
                className="btn btn-sm d-flex align-items-center gap-2 fw-bold"
                style={{
                  borderRadius: '8px',
                  padding: '7px 14px',
                  border: 'none',
                  fontSize: '12px',
                  transition: 'all 0.2s',
                  background: agentViewMode === opt.id
                    ? 'linear-gradient(135deg, #3b82f6, #6366f1)'
                    : 'transparent',
                  color: agentViewMode === opt.id ? 'white' : 'var(--bs-gray-500)',
                  boxShadow: agentViewMode === opt.id ? '0 2px 8px rgba(99,102,241,0.35)' : 'none',
                }}>
                <i className={`bi ${opt.icon}`} style={{ fontSize: 13 }}></i>
                <span className="d-none d-md-inline">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── VIEW CONTENT ── */}
        {agentViewMode === 'kanban'
          ? renderAgentWorkspaceKanban()
          : agentViewMode === 'table'
            ? renderAgentWorkspaceTable()
            : renderAgentWorkspaceList()
        }
      </div>
    );
  }

  const fetchRemainingData = async () => {
    try {
      const response = await getSubmissionsByCampaign(data.id as string, `all=true`);
      const allLeads = response?.data || [];
      const remaining = allLeads.filter((lead: any) => lead.status_submission !== 'Close').length;
      setRemainingDataCount(remaining);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!isAgent && data.dialMode === 'Predictive') {
      fetchRemainingData();
    }
  }, [data.id, data.dialMode, isAgent]);

  const handleSavePredictiveSettings = async () => {
    try {
      setLoading(true);
      const updatePayload = {
        ...data,
        pacingRatio: Number(predictiveSettings.pacingRatio),
        phoneCategory: predictiveSettings.phoneCategory,
        durationPerTouch: Number(predictiveSettings.durationPerTouch),
        retries: Number(predictiveSettings.retries),
        acwTime: Number(predictiveSettings.acwTime),
        predictiveWaitSound: predictiveSettings.predictiveWaitSound,
        autoDialActive: isPredictiveActive,
        formFields: data.formFields || []
      };
      await updateData(updatePayload as any, data.id as string);
      toast.success('Pengaturan Predictive berhasil disimpan!');
    } catch (err) {
      console.error(err);
      toast.error('Gagal menyimpan pengaturan.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPredictiveData = async () => {
    if (!window.confirm('Apakah Anda yakin ingin mereset data yang gagal pada siklus sebelumnya?')) return;
    try {
      setLoading(true);
      await resetPredictiveData(data.id as string);
      toast.success('Data berhasil direset!');
      fetchRemainingData();
      fetchLeads(currentPage);
    } catch (err) {
      console.error(err);
      toast.error('Gagal mereset data.');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePredictiveStatus = async () => {
    try {
      setLoading(true);
      const newStatus = !isPredictiveActive;
      await togglePredictiveStatus(data.id as string, newStatus);
      setIsPredictiveActive(newStatus);
      toast.success(`Proses predictive berhasil ${newStatus ? 'dimulai' : 'dijeda'}.`);
    } catch (err) {
      console.error(err);
      toast.error('Gagal mengubah status predictive.');
    } finally {
      setLoading(false);
    }
  };

  const handleTestCallSubmit = async () => {
    try {
      setIsTestCalling(true);
      await testCall(data.id as string, testCallTimeout);
      toast.success('Panggilan tes untuk semua leads berhasil dimulai! Silakan tunggu telepon dari sistem.');
      setIsTestCallModalOpen(false);
      fetchLeads(currentPage);
    } catch (err) {
      console.error(err);
      toast.error('Gagal memulai test call.');
    } finally {
      setIsTestCalling(false);
    }
  };

  const fetchCampaignListForTransfer = async () => {
    try {
      const res = await getData('items_per_page=1000');
      if (res && res.data) {
        setAvailableCampaigns(res.data.filter((c: any) => c.id !== data.id));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleTransferSubmit = async () => {
    if (!transferTargetId) {
      toast.warning('Pilih campaign tujuan terlebih dahulu');
      return;
    }
    try {
      setIsTransferring(true);
      const res = await transferLeads(data.id as string, transferTargetId, transferStatusFilter);
      toast.success(`Berhasil memindahkan ${res.transferredCount || 0} data leads!`);
      setIsTransferModalOpen(false);
      fetchLeads(currentPage);
    } catch (err) {
      console.error(err);
      toast.error('Gagal mentransfer data leads.');
    } finally {
      setIsTransferring(false);
    }
  };

  const renderPredictiveSettingsPanel = () => {
    if (isAgent || data.dialMode !== 'Predictive') return null;

    return (
      <div className='card mb-5 mb-xl-8 shadow-sm' style={{ borderRadius: '16px', overflow: 'hidden' }}>
        <div className='card-header px-4 px-md-7 py-3' style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #312e81 100%)' }}>
          <h3 className='card-title align-items-start flex-column text-white m-0'>
            <span className='fw-bolder fs-3 mb-1'>Setting Predictive</span>
            <span className='text-white opacity-75 fw-bold fs-8 fs-md-7'>
              Digunakan untuk mengatur proses predictive dialing.
            </span>
          </h3>
        </div>
        <div className='card-body p-4 p-md-7'>
          <div className='row g-4 mb-6'>
            <div className='col-12 col-lg-6'>
              <div className='d-flex flex-column gap-1'>
                <div className='fw-bolder fs-6 text-gray-800 mb-1'>Total Remaining Data</div>
                <div className='text-muted fs-8 fs-md-7 mb-3'>
                  Total Remaining Data menampilkan jumlah data yang belum dipanggil. Jika Anda menekan tombol "Reset Data", semua data yang gagal pada siklus sebelumnya akan dicoba kembali.
                </div>
                <div className='d-flex flex-wrap align-items-center gap-3'>
                  <span className='badge badge-light-primary fs-6 fs-md-5 fw-bolder py-2 px-4'>
                    {remainingDataCount} Data
                  </span>
                  <button
                    className='btn btn-sm btn-light-primary fw-bolder'
                    onClick={handleResetPredictiveData}
                    disabled={loading || remainingDataCount === 0}>
                    {loading ? <span className='spinner-border spinner-border-sm'></span> : <i className='bi bi-arrow-clockwise me-1'></i>} Reset Data
                  </button>
                </div>
              </div>
            </div>
            <div className='col-12 col-lg-6 d-flex flex-column border-0 border-lg-start border-gray-200 ps-0 ps-lg-6 mt-4 mt-lg-0'>
              <div className='fw-bolder fs-6 text-gray-800 mb-1'>Start/Pause Call</div>
              <div className='text-muted fs-8 fs-md-7 mb-3'>
                Start/Pause Call menunjukkan status proses predictive saat ini. Jika Anda menekan tombol "Proses", proses predictive call akan mulai berjalan.
              </div>
              <div className='d-flex flex-wrap align-items-center gap-3'>
                <span className={`badge badge-light-${isPredictiveActive ? 'success' : 'warning'} fs-6 fs-md-5 fw-bolder py-2 px-4`}>
                  Status: {isPredictiveActive ? 'Running' : 'Paused'}
                </span>
                <button
                  className={`btn btn-sm fw-bolder ${isPredictiveActive ? 'btn-light-warning' : 'btn-light-success'}`}
                  onClick={handleTogglePredictiveStatus}
                  disabled={loading}>
                  {loading ? <span className='spinner-border spinner-border-sm'></span> : <i className={`bi ${isPredictiveActive ? 'bi-pause-fill' : 'bi-play-fill'} me-1`}></i>}
                  {isPredictiveActive ? 'Pause Call' : 'Proses Call'}
                </button>
              </div>
            </div>
          </div>

          <div className='separator border-gray-300 mb-6'></div>
          <div className='row g-3 mb-4'>
            <div className='col-12 col-sm-6 col-md-4 col-xl-2 mb-3'>
              <label className='form-label fw-bolder text-gray-800 fs-8'>Pacing Ratio</label>
              <div className='input-group input-group-sm'>
                <span className='input-group-text bg-light-primary border-primary border-opacity-25'>
                  <i className='bi bi-speedometer2 text-primary'></i>
                </span>
                <input
                  type='number'
                  className='form-control form-control-solid border-primary border-opacity-25'
                  value={predictiveSettings.pacingRatio}
                  onChange={(e) => setPredictiveSettings({ ...predictiveSettings, pacingRatio: Number(e.target.value) })}
                  min={1}
                  max={10}
                />
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-4 col-xl-2 mb-3'>
              <label className='form-label fw-bolder text-gray-800 fs-8'>Phone Category</label>
              <select
                className='form-select form-select-sm form-select-solid border-primary border-opacity-25'
                value={predictiveSettings.phoneCategory}
                onChange={(e) => setPredictiveSettings({ ...predictiveSettings, phoneCategory: e.target.value })}
              >
                <option value=''>- Select Field -</option>
                {data.formFields?.filter(f => f.fieldType === 'Phone' || f.fieldType === 'Number').map(f => (
                  <option key={f.fieldName} value={f.fieldName}>{f.fieldName}</option>
                ))}
              </select>
            </div>
            <div className='col-12 col-sm-6 col-md-4 col-xl-2 mb-3'>
              <label className='form-label fw-bolder text-gray-800 fs-8'>Duration/Touch (s)</label>
              <div className='input-group input-group-sm'>
                <span className='input-group-text bg-light-primary border-primary border-opacity-25'>
                  <i className='bi bi-stopwatch text-primary'></i>
                </span>
                <input
                  type='number'
                  className='form-control form-control-solid border-primary border-opacity-25'
                  value={predictiveSettings.durationPerTouch}
                  onChange={(e) => setPredictiveSettings({ ...predictiveSettings, durationPerTouch: Number(e.target.value) })}
                  min={5}
                />
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-4 col-xl-2 mb-3'>
              <label className='form-label fw-bolder text-gray-800 fs-8'>Retries/Cycle</label>
              <div className='input-group input-group-sm'>
                <span className='input-group-text bg-light-primary border-primary border-opacity-25'>
                  <i className='bi bi-arrow-repeat text-primary'></i>
                </span>
                <input
                  type='number'
                  className='form-control form-control-solid border-primary border-opacity-25'
                  value={predictiveSettings.retries}
                  onChange={(e) => setPredictiveSettings({ ...predictiveSettings, retries: Number(e.target.value) })}
                  min={1}
                />
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-4 col-xl-2 mb-3'>
              <label className='form-label fw-bolder text-gray-800 fs-8'>ACW Time (s)</label>
              <div className='input-group input-group-sm'>
                <span className='input-group-text bg-light-primary border-primary border-opacity-25'>
                  <i className='bi bi-clock-history text-primary'></i>
                </span>
                <input
                  type='number'
                  className='form-control form-control-solid border-primary border-opacity-25'
                  value={predictiveSettings.acwTime}
                  onChange={(e) => setPredictiveSettings({ ...predictiveSettings, acwTime: Number(e.target.value) })}
                  min={0}
                />
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-4 col-xl-2 mb-3'>
              <label className='form-label fw-bolder text-gray-800 fs-8'>Wait Sound</label>
              <select
                className='form-select form-select-sm form-select-solid border-primary border-opacity-25'
                value={predictiveSettings.predictiveWaitSound.startsWith('custom:') ? 'custom' : predictiveSettings.predictiveWaitSound}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'custom') {
                    setPredictiveSettings({ ...predictiveSettings, predictiveWaitSound: 'custom:' });
                  } else {
                    setPredictiveSettings({ ...predictiveSettings, predictiveWaitSound: val });
                  }
                }}
              >
                <option value='ringing'>Nada Tut (Ringing)</option>
                <option value='moh'>Music on Hold (Default)</option>
                <option value='custom'>Custom Audio (.wav)</option>
                <option value='none'>Tanpa Suara (Hening)</option>
              </select>
              {predictiveSettings.predictiveWaitSound.startsWith('custom:') && (
                <div className="mt-2">
                  <input
                    type="file"
                    className="form-control form-control-sm"
                    accept=".wav"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const apiUrl = process.env.REACT_APP_THEME_API_URL || process.env.REACT_APP_API_URL || '';
                        const res = await axios.post(`${apiUrl}/call/campaign/${data.id}/upload-predictive-sound`, formData);
                        if (res.data?.data?.fileName) {
                          setPredictiveSettings(prev => ({ ...prev, predictiveWaitSound: `custom:${res.data.data.fileName}` }));
                          toast.success('Audio berhasil diupload. Silakan Simpan Pengaturan.');
                        }
                      } catch (err: any) {
                        toast.error(err.response?.data?.message || 'Gagal mengupload audio');
                      }
                    }}
                  />
                  {predictiveSettings.predictiveWaitSound !== 'custom:' && (
                    <small className="text-success d-block mt-1 fs-9">
                      Terpilih: {predictiveSettings.predictiveWaitSound.replace('custom:', '').replace('predictive_sound/', '')}
                    </small>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className='d-flex justify-content-stretch justify-content-sm-end mt-4'>
            <button className='btn btn-primary fw-bolder px-8 w-100 w-sm-auto' onClick={handleSavePredictiveSettings} disabled={loading}>
              {loading ? <span className='spinner-border spinner-border-sm me-2'></span> : <i className='bi bi-check2-circle fs-4 me-2'></i>}
              Simpan Pengaturan
            </button>
          </div>

          <div className='separator border-gray-300 mt-6 mb-6'></div>

          <div className='mt-6'>
            <MonitoringPredictive campaignId={String(data.id)} />
          </div>
        </div>
      </div>
    );
  };

  const handleResetVoiceBlastData = async () => {
    if (!window.confirm('Apakah Anda yakin ingin mereset data Voice Blast yang gagal pada siklus sebelumnya?')) return;
    try {
      setAutoDialLoading(true);
      await resetVoiceBlastData(data.id as string);
      toast.success('Data Voice Blast berhasil direset!');
      fetchRemainingData();
      fetchLeads(currentPage);
    } catch (err) {
      console.error(err);
      toast.error('Gagal mereset data Voice Blast.');
    } finally {
      setAutoDialLoading(false);
    }
  };

  const renderVoiceBlastSettingsPanel = () => {
    if (isAgent || data.dialMode !== 'Voice Blast') return null;

    return (
      <div className='card mb-5 mb-xl-8 shadow-sm' style={{ borderRadius: '16px', overflow: 'hidden' }}>
        <div className='card-header px-4 px-md-7 py-3' style={{ background: 'linear-gradient(135deg, #047857 0%, #065f46 100%)' }}>
          <h3 className='card-title align-items-start flex-column text-white m-0'>
            <span className='fw-bolder fs-3 mb-1'>Setting Voice Blast</span>
            <span className='text-white opacity-75 fw-bold fs-8 fs-md-7'>
              Digunakan untuk memantau status dan mereset antrean panggilan Voice Blast.
            </span>
          </h3>
        </div>
        <div className='card-body p-4 p-md-7'>
          <div className='row g-4 mb-4'>
            <div className='col-12 col-lg-6'>
              <div className='d-flex flex-column gap-1'>
                <div className='fw-bolder fs-6 text-gray-800 mb-1'>Total Remaining Data</div>
                <div className='text-muted fs-8 fs-md-7 mb-3'>
                  Total Remaining Data menampilkan jumlah data yang belum berhasil dihubungi. Menekan "Reset Data" akan mengembalikan status semua lead yang gagal agar dapat di-blast kembali.
                </div>
                <div className='d-flex flex-wrap align-items-center gap-3'>
                  <span className='badge badge-light-primary fs-6 fs-md-5 fw-bolder py-2 px-4'>
                    {remainingDataCount} Data
                  </span>
                  <button
                    className='btn btn-sm btn-light-primary fw-bolder'
                    onClick={handleResetVoiceBlastData}
                    disabled={autoDialLoading || remainingDataCount === 0}>
                    {autoDialLoading ? <span className='spinner-border spinner-border-sm'></span> : <i className='bi bi-arrow-clockwise me-1'></i>} Reset Data
                  </button>
                </div>
              </div>
            </div>
            <div className='col-12 col-lg-6 d-flex flex-column border-0 border-lg-start border-gray-200 ps-0 ps-lg-6 mt-4 mt-lg-0'>
              <div className='fw-bolder fs-6 text-gray-800 mb-1'>Status Voice Blast</div>
              <div className='text-muted fs-8 fs-md-7 mb-3'>
                Menunjukkan status operasional Voice Blast saat ini. Jika aktif, mesin akan memanggil nomor tujuan secara otomatis.
              </div>
              <div className='d-flex flex-wrap align-items-center gap-3'>
                <span className={`badge badge-light-${voiceBlastActive ? 'success' : 'danger'} fs-6 fs-md-5 fw-bolder py-2 px-4`}>
                  Status: {voiceBlastActive ? 'Running' : 'Stopped'}
                </span>
                <button
                  className={`btn btn-sm fw-bolder ${voiceBlastActive ? 'btn-light-danger' : 'btn-light-success'}`}
                  onClick={() => handleToggleVoiceBlast(!voiceBlastActive)}
                  disabled={autoDialLoading || totalData === 0}>
                  {autoDialLoading ? <span className='spinner-border spinner-border-sm'></span> : <i className={`bi ${voiceBlastActive ? 'bi-stop-fill' : 'bi-play-fill'} me-1`}></i>}
                  {voiceBlastActive ? 'Stop Voice Blast' : 'Start Voice Blast'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='d-flex flex-column gap-5'>

      {/* DATABASE CARD */}
      <div 
        className='card border-0 rounded-4 shadow-sm overflow-hidden'
        style={{
          border: '1px solid var(--bs-border-color)'
        }}
      >

        {/* ── MODERN DATABASE HEADER ── */}
        <div 
          className='px-5 px-md-6 py-4 d-flex flex-column flex-xl-row align-items-stretch align-items-xl-center justify-content-between gap-4'
          style={{
            background: 'linear-gradient(135deg, #0d1b4b 0%, #152766 50%, #1a3a8a 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          {/* LEFT: Title + Interactive Status Pills */}
          <div className='d-flex align-items-center justify-content-between justify-content-sm-start gap-4 flex-wrap'>
            <div className='d-flex align-items-center gap-3'>
              <div 
                className='d-flex align-items-center justify-content-center rounded-3'
                style={{ width: 42, height: 42, background: 'rgba(255,255,255,0.12)', color: '#ffffff' }}
              >
                <Database size={20} />
              </div>
              <div>
                <div className='d-flex align-items-center gap-2'>
                  <h4 className='fw-bold text-white mb-0 fs-5'>Leads Database</h4>
                  <span className='badge bg-white bg-opacity-20 text-white rounded-pill px-2.5 py-1 fs-9 fw-semibold'>
                    {totalData} Total
                  </span>
                </div>
                <span className='text-white text-opacity-70 fs-8'>
                  Daftar kontak data nasabah & histori panggilan
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className='d-none d-xl-block' style={{ width: 1, height: 36, background: 'rgba(255,255,255,0.18)' }}></div>

            {/* Status Pills */}
            <div className='d-none d-sm-flex align-items-center gap-2 flex-wrap'>
              {[
                { label: 'Semua', val: '', count: totalData, color: '#60a5fa' },
                { label: 'Ready', val: 'ready', count: groupedData['Ready']?.length || 0, color: '#38bdf8' },
                { label: 'Close', val: 'close', count: groupedData['Close']?.length || 0, color: '#4ade80' },
              ].map(s => {
                const isActive = (s.val === '' && !searchStatus) || (searchStatus.toLowerCase() === s.val);
                return (
                  <button
                    type='button'
                    key={s.label}
                    onClick={() => {
                      setSearchStatus(s.val);
                      setCurrentPage(1);
                    }}
                    className='btn btn-xs rounded-pill d-inline-flex align-items-center gap-2 px-3 py-1.5 border-0 transition-all'
                    style={{
                      background: isActive ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)',
                      color: '#ffffff',
                      border: isActive ? '1px solid rgba(255,255,255,0.4)' : '1px solid rgba(255,255,255,0.12)',
                      boxShadow: isActive ? '0 0 12px rgba(255,255,255,0.2)' : 'none'
                    }}
                    title={`Filter: ${s.label}`}
                  >
                    <span 
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: s.color,
                        display: 'inline-block',
                        boxShadow: `0 0 6px ${s.color}`
                      }}
                    />
                    <span className='fw-bold fs-8'>{s.label}</span>
                    <span className='badge bg-black bg-opacity-25 text-white rounded-pill px-1.5 py-0.5 fs-9 fw-semibold'>
                      {s.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT: Search, Filter, Auto Dialer & Actions */}
          <div className='d-flex flex-wrap align-items-center gap-2.5 w-100 w-xl-auto justify-content-start justify-content-xl-end'>
            {/* Search Box */}
            <div className='position-relative' style={{ minWidth: 160, maxWidth: 240, flex: '1 1 auto' }}>
              <Search size={14} className='position-absolute top-50 translate-middle-y text-muted' style={{ left: 12 }} />
              <input
                type='text'
                className='form-control form-control-sm ps-9 rounded-pill bg-white text-gray-800'
                placeholder='Cari nasabah...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchLeads(1)}
                style={{ height: 36, border: 'none' }}
              />
            </div>

            {/* Status Select Filter */}
            {agentViewMode !== 'kanban' && (
              <select
                className='form-select form-select-sm rounded-pill bg-white text-gray-800 fw-semibold'
                value={searchStatus}
                onChange={(e) => {
                  setSearchStatus(e.target.value)
                  setCurrentPage(1)
                }}
                style={{ width: 'auto', minWidth: 120, height: 36, border: 'none' }}
              >
                <option value=''>Semua Status</option>
                <option value='ready'>Ready</option>
                <option value='close'>Close</option>
              </select>
            )}

            {/* Auto Dialer Controls (when dialMode === 'Auto') */}
            {data.dialMode === 'Auto' && (
              <div className='d-flex align-items-center gap-1.5 bg-white bg-opacity-15 p-1 rounded-pill'>
                <select
                  className='form-select form-select-sm rounded-pill bg-white text-gray-800 fw-bold border-0 px-2'
                  title='Delay'
                  value={autoDialDelay}
                  disabled={autoDialActive}
                  onChange={(e) => setAutoDialDelay(Number(e.target.value))}
                  style={{ width: 65, height: 28, fontSize: '11px' }}
                >
                  {[3, 5, 10, 15, 30].map(v => <option key={v} value={v}>{v}s</option>)}
                </select>

                <button
                  type='button'
                  className={`btn btn-xs rounded-pill fw-bold text-white d-inline-flex align-items-center gap-1.5 px-3 py-1.5 border-0 ${
                    autoDialActive ? 'btn-danger' : 'btn-success'
                  }`}
                  onClick={() => handleToggleAutoDial(!autoDialActive)}
                  disabled={autoDialLoading || totalData === 0}
                  style={{ height: 28 }}
                >
                  {autoDialLoading ? (
                    <span className='spinner-border spinner-border-sm' />
                  ) : autoDialActive ? (
                    <>
                      <Pause size={12} />
                      <span>Stop Auto</span>
                    </>
                  ) : (
                    <>
                      <Play size={12} />
                      <span>Start Auto</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Non-Agent Tool Buttons */}
            {!isAgent && !onTriggerProcessed && (
              <div className='d-flex align-items-center gap-1.5'>
                <button
                  type='button'
                  className='btn btn-xs btn-warning text-white fw-bold rounded-pill d-inline-flex align-items-center gap-1.5 px-3 py-1.5 shadow-sm'
                  title='Test Call Leads'
                  onClick={() => setIsTestCallModalOpen(true)}
                  style={{ height: 34 }}
                >
                  <PhoneOutgoing size={13} />
                  <span className='d-none d-md-inline'>Test Call</span>
                </button>

                <button
                  type='button'
                  className='btn btn-xs btn-primary fw-bold rounded-pill d-inline-flex align-items-center gap-1.5 px-3 py-1.5 shadow-sm'
                  title='Transfer Leads'
                  onClick={() => {
                    fetchCampaignListForTransfer()
                    setIsTransferModalOpen(true)
                  }}
                  style={{ height: 34, background: '#6366f1', border: 'none' }}
                >
                  <ArrowLeftRight size={13} />
                  <span className='d-none d-md-inline'>Transfer</span>
                </button>

                <button
                  type='button'
                  className='btn btn-xs btn-light bg-white text-gray-800 fw-bold rounded-circle d-inline-flex align-items-center justify-content-center p-0 shadow-sm'
                  title='Download Template Excel'
                  onClick={handleExportTemplate}
                  style={{ width: 34, height: 34 }}
                >
                  <Download size={14} />
                </button>

                <input type='file' ref={fileInputRef} onChange={handleImportLeads} className='d-none' accept='.xlsx,.xls' />
                <button
                  type='button'
                  className='btn btn-xs btn-success text-white fw-bold rounded-pill d-inline-flex align-items-center gap-1.5 px-3 py-1.5 shadow-sm'
                  title='Import Leads (.xlsx)'
                  onClick={() => fileInputRef.current?.click()}
                  style={{ height: 34 }}
                >
                  <Upload size={13} />
                  <span>Import</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className='card-body p-0'>
          {/* Bulk Action Toolbar */}
          {!isAgent && selectedLeads.size > 0 && (
            <div className="px-6 py-3 bg-light-primary border-bottom border-primary border-opacity-25 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <span className="fw-bold text-primary fs-7 d-flex align-items-center gap-2">
                <Sparkles size={16} />
                <span>{selectedLeads.size} Leads Terpilih</span>
              </span>
              <div className="d-flex gap-2">
                <button className="btn btn-xs btn-primary rounded-pill shadow-sm" onClick={handleBulkAssign} disabled={loading}>
                  <UserPlus size={13} className="me-1" /> Assign Agent
                </button>
                <button className="btn btn-xs btn-warning text-white rounded-pill shadow-sm" onClick={handleBulkStatus} disabled={loading}>
                  <RefreshCw size={13} className="me-1" /> Ganti Status
                </button>
                <button className="btn btn-xs btn-danger rounded-pill shadow-sm" onClick={handleBulkDelete} disabled={loading}>
                  <Trash2 size={13} className="me-1" /> Hapus Leads
                </button>
              </div>
            </div>
          )}

          {!isAgent ? (
            <div className='table-responsive'>
              <table className='table table-hover table-row-dashed align-middle gs-6 gy-3.5 mb-0'>
                <thead>
                  <tr className='fw-bold fs-8 text-uppercase text-muted border-bottom border-gray-200' style={{ background: 'var(--bs-gray-100)' }}>
                    {!isAgent && (
                      <th className='w-40px sticky-column py-3' style={{ left: 0 }}>
                        <div className="form-check form-check-sm form-check-custom form-check-solid">
                          <input className="form-check-input" type="checkbox" onChange={toggleSelectAll} checked={selectedLeads.size > 0 && selectedLeads.size === displayData.length} />
                        </div>
                      </th>
                    )}
                    <th className='min-w-100px sticky-column cursor-pointer py-3' style={{ left: !isAgent ? 40 : 0 }} onClick={() => handleSort('status_submission')}>
                      STATUS {renderSortIcon('status_submission')}
                    </th>
                    {!isAgent && (
                      <th className='min-w-140px cursor-pointer py-3' onClick={() => handleSort('assigned_to')}>
                        ASSIGNED TO {renderSortIcon('assigned_to')}
                      </th>
                    )}
                    {!isAgent && activeTab === 'all' && (
                      <th className='min-w-110px py-3'>CHANGE STATUS</th>
                    )}
                    <th className='min-w-120px cursor-pointer py-3' onClick={() => handleSort('call_count')}>
                      CALL COUNT {renderSortIcon('call_count')}
                    </th>
                    {(data.dialMode === 'Auto' || data.dialMode === 'Predictive') && (
                      <th className='min-w-120px cursor-pointer py-3' onClick={() => handleSort('call_disposition')}>
                        DISPOSITION {renderSortIcon('call_disposition')}
                      </th>
                    )}
                    {sortedFields.map((f, i) => (
                      <th key={i} className='min-w-140px cursor-pointer py-3' onClick={() => handleSort(f.fieldName || '')}>
                        {f.fieldName?.toUpperCase()} {renderSortIcon(f.fieldName || '')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className='fs-7'>
                  {displayData.map((row, idx) => {
                    const rtData = realtimeDisposition[String(row.id)]
                    const isCallingRt = rtData?.status === 'calling' || row.status_submission === 'calling'
                    const dispositionRt = rtData?.disposition ?? row.call_disposition

                    const rowBgClass = importPreview.length > 0 ? 'bg-light-success' :
                      isCallingRt ? 'bg-light-success' :
                        rtData?.disposition === 'NO ANSWER' || rtData?.disposition === 'BUSY' ? 'bg-light-warning' :
                          rtData?.disposition === 'FAILED' ? 'bg-light-danger' : '';

                    return (
                      <tr key={idx} className={rowBgClass} style={{ transition: 'background 0.5s ease' }}>

                        {!isAgent && (
                          <td className={clsx('sticky-column', rowBgClass)} style={{ left: 0 }}>
                            <div className="form-check form-check-sm form-check-custom form-check-solid">
                              <input className="form-check-input" type="checkbox" onChange={() => toggleSelectLead(String(row.id))} checked={selectedLeads.has(String(row.id))} />
                            </div>
                          </td>
                        )}
                        <td className={clsx('sticky-column', rowBgClass)} style={{ left: !isAgent ? 40 : 0 }}>
                          {importPreview.length > 0 ? (
                            <span className='badge badge-light-warning'>PREVIEW</span>
                          ) : (
                            <div className='d-flex align-items-center'>

                              {(row.assigned_to === null) && (
                                <button
                                  className='btn btn-icon btn-sm btn-light-danger me-2'
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to delete this data?')) {
                                      handleDelete(row.id)
                                    }
                                  }}
                                  title='Delete User'
                                  disabled={deleteSubmission.isLoading}
                                >
                                  {deleteSubmission.isLoading ? (
                                    <span className='spinner-border spinner-border-sm' role='status'></span>
                                  ) : (
                                    <Trash2 size={16} />
                                  )}
                                </button>
                              )}


                              <span className={clsx(
                                'badge fw-bold px-4 py-3 text-uppercase',
                                row.status_submission?.toLowerCase() === 'ready' ? 'badge-light-primary' :
                                  row.status_submission?.toLowerCase() === 'calling' ? 'badge-primary' :
                                    row.status_submission?.toLowerCase() === 'close' ? 'badge-light-success' : 'badge-light-warning'
                              )} style={{ borderRadius: '8px', minWidth: '85px', textAlign: 'center' }}>
                                {row.status_submission || 'READY'}
                              </span>

                            </div>
                          )}
                        </td>

                        {!isAgent && (
                          <td className='text-gray-600 fw-medium'>
                            <span className="badge badge-light fw-bold fs-7" style={{ borderRadius: '6px' }}>
                              {row.assigned_to_name || row.assigned_to || <span className="text-muted">Unassigned</span>}
                            </span>
                          </td>
                        )}

                        {!isAgent && activeTab === 'all' && (
                          <td className={clsx('sticky-column', rowBgClass)} style={{ left: 140 }}>
                            <select
                              className="form-select form-select-sm form-select-solid"
                              style={{ minWidth: '110px' }}
                              value={row.status_submission || 'Ready'}
                              onChange={async (e) => {
                                try {
                                  await updateLeadStatus(row.id, e.target.value)
                                  toast.success('Status updated!')
                                  fetchLeads(currentPage)
                                } catch (err) {
                                  toast.error('Failed to update status')
                                }
                              }}
                            >
                              <option value="Ready">Ready</option>
                              <option value="Close">Close</option>
                              <option value="Calling">Calling</option>
                            </select>
                          </td>
                        )}

                        <td className='text-gray-600 fw-medium'>
                          {importPreview.length > 0 ? (
                            <span className='text-muted fs-7'>-</span>
                          ) : (
                            <span className={clsx(
                              'badge fw-bolder px-3 py-2 fs-7',
                              row.call_count === 0 && 'badge-light-success',
                              row.call_count > 0 && row.call_count < (data.retries ?? 3) && 'badge-light-warning',
                              row.call_count >= (data.retries ?? 3) && 'badge-light-danger text-danger'
                            )} style={{ borderRadius: '6px' }} title={`Sudah ditelepon ${row.call_count} kali`}>
                              {row.call_count >= (data.retries ?? 3) ? `${data.retries ?? 3}/${data.retries ?? 3} (Max)` : `${row.call_count}/${data.retries ?? 3}`}
                            </span>
                          )}
                        </td>

                        {/* DISPOSITION BADGE — Auto/Predictive only */}
                        {(data.dialMode === 'Auto' || data.dialMode === 'Predictive') && (
                          <td className='text-center'>
                            {isCallingRt ? (
                              <span className='badge badge-light-success px-3 py-2 d-flex align-items-center gap-1' style={{ borderRadius: 8 }}>
                                <span className='spinner-grow spinner-grow-sm' style={{ width: 8, height: 8 }}></span>
                                Calling...
                              </span>
                            ) : dispositionRt === 'ANSWERED' ? (
                              <span className='badge badge-light-success px-3 py-2' style={{ borderRadius: 8 }}>
                                <i className='bi bi-check-circle-fill text-success me-1'></i>Connected
                              </span>
                            ) : dispositionRt === 'NO ANSWER' ? (
                              <span className='badge badge-light-warning px-3 py-2' style={{ borderRadius: 8 }}>
                                <i className='bi bi-telephone-x text-warning me-1'></i>No Answer
                              </span>
                            ) : dispositionRt === 'BUSY' ? (
                              <span className='badge badge-light-warning px-3 py-2' style={{ borderRadius: 8 }}>
                                <i className='bi bi-slash-circle text-warning me-1'></i>Busy
                              </span>
                            ) : dispositionRt === 'FAILED' ? (
                              <span className='badge badge-light-danger px-3 py-2' style={{ borderRadius: 8 }}>
                                <i className='bi bi-x-circle-fill text-danger me-1'></i>Failed
                              </span>
                            ) : (
                              <span className='badge badge-light-secondary px-3 py-2' style={{ borderRadius: 8 }}>-</span>
                            )}
                          </td>
                        )}

                        {sortedFields.map((f: any, i: number) => {
                          const isPhoneField = f.fieldType === 'Phone';
                          const cellValue = row[f.fieldName || ''] || '-';

                          return (
                            <td key={i} className='text-gray-600 fw-medium'>
                              <div className='d-flex align-items-center'>

                                {isPhoneField && f.canCall && cellValue !== '-' && (
                                  <button
                                    className={clsx(
                                      'btn btn-icon btn-md me-2',
                                      (row.call_count >= (data.retries ?? 3)) ? row.status_submission === 'Close' ? 'btn-bg-light btn-active-color-primary' : 'btn-light-secondary opacity-50' : 'btn-bg-light btn-active-color-primary'
                                    )}
                                    style={{ width: '28px', height: '28px', borderRadius: '8px' }}
                                    title={(row.call_count >= (data.retries ?? 3)) ? `Batas maksimal panggilan tercapai (${data.retries ?? 3}x)` : `Call ${cellValue}`}
                                    disabled={(row.call_count >= (data.retries ?? 3) || importPreview.length > 0) ? row.status_submission === 'Close' ? false : true : false}
                                    onClick={() => {
                                      const tempLead = { ...row };
                                      if (row.status_submission === 'Close') {
                                        callClosed(tempLead, f.fieldName);
                                      } else {
                                        openCallDrawer(tempLead, f.fieldName);
                                      }
                                    }}
                                  >
                                    <i className={clsx(
                                      'bi bi-telephone-fill fs-8',
                                      row.call_count >= (data.retries ?? 3) ? row.status_submission === 'Close' ? 'text-success' : 'text-gray-400' : 'text-success'
                                    )}></i>
                                  </button>
                                )}

                                {isPhoneField && !f.canCall && cellValue !== '-' && (
                                  <i className='bi bi-telephone-x fs-8 text-gray-400 ms-2 me-2' title="Cannot call this field"></i>
                                )}
                                <span>{cellValue}</span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            renderAgentWorkspace()
          )}
        </div>

        {/* FOOTER */}
        {!importPreview.length && totalData > 0 && !isAgent && (
          <div className='card-footer py-4 px-4 px-md-6 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 bg-body border-0 border-top border-gray-200' style={{ borderBottomLeftRadius: '20px', borderBottomRightRadius: '20px' }}>
            <div className='d-flex flex-wrap align-items-center justify-content-center gap-3 text-gray-700 fs-8 fs-md-7 fw-bold bg-light px-3 px-md-4 py-2' style={{ borderRadius: '10px' }}>
              <div>
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalData)} of {totalData} leads
              </div>
              <div className='d-flex align-items-center gap-2 border-start border-gray-300 ps-3 ps-md-4'>
                <span className='text-muted fw-semibold'>Rows:</span>
                <select
                  className='form-select form-select-solid form-select-sm py-1 px-2 fw-bolder cursor-pointer'
                  style={{ width: '70px', background: 'transparent' }}
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <ul className='pagination pagination-circle mb-0 flex-wrap justify-content-center'>
              <li className={clsx('page-item previous', currentPage === 1 && 'disabled')}>
                <button className='page-link' onClick={() => fetchLeads(currentPage - 1)}>
                  <i className='bi bi-chevron-left fs-7'></i>
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                (i + 1 === 1 || i + 1 === totalPages || Math.abs(i + 1 - currentPage) <= 1) && (
                  <li key={i} className={clsx('page-item mx-1', currentPage === i + 1 && 'active')}>
                    <button className='page-link fw-bold' onClick={() => fetchLeads(i + 1)}>{i + 1}</button>
                  </li>
                )
              ))}
              <li className={clsx('page-item next', currentPage === totalPages && 'disabled')}>
                <button className='page-link' onClick={() => fetchLeads(currentPage + 1)}>
                  <i className='bi bi-chevron-right fs-7'></i>
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* DRAWER & OVERLAY */}
      <div className={clsx(
        'bg-body shadow-lg position-fixed top-0 end-0 h-100 z-index-100 d-flex flex-column',
        isDrawerOpen ? 'animate__animated animate__slideInRight' : 'd-none'
      )} style={{ width: '100%', maxWidth: 'min(550px, 100vw)', zIndex: 9999, borderTopLeftRadius: '24px', borderBottomLeftRadius: '24px' }}>
        <div className='card w-100 h-100 rounded-0 border-0' style={{ borderTopLeftRadius: '24px', borderBottomLeftRadius: '24px' }}>
          <div className='card-header px-5 px-md-7 flex-grow-0 border-0 pt-6 pb-2'>
            <div className='card-title m-0'>
              <div className='d-flex align-items-center'>
                <div className='symbol symbol-40px symbol-md-50px me-3 me-md-4'>
                  <div className='symbol-label fs-3 fs-md-2 fw-bold bg-light-primary text-primary'>
                    {selectedLead?.full_name?.charAt(0) || 'C'}
                  </div>
                </div>
                <div className='d-flex flex-column'>
                  <span className='fs-4 fs-md-3 fw-bolder text-gray-900'>Call Result</span>
                  <span className='text-muted fw-bold fs-8 fs-md-7 text-truncate' style={{ maxWidth: '200px' }}>{selectedLead?.full_name}</span>
                </div>
              </div>
            </div>
            <div className='card-toolbar'>
              <button className='btn btn-sm btn-icon btn-light' onClick={() => setIsDrawerOpen(false)} style={{ borderRadius: '12px' }}>
                <i className='bi bi-x fs-1'></i>
              </button>
            </div>
          </div>

          <div className='card-body scroll-y px-5 px-md-10 pt-4 pb-6 hover-scroll-overlay-y flex-grow-1' style={{ overflowY: 'auto' }}>
            {renderFormBody()}
          </div>

          <div className='card-footer py-4 px-5 px-md-10 d-flex flex-stack bg-body border-0'>
            <button type='button' className='btn btn-light fw-bold px-6 px-md-8'
              style={{ borderRadius: '12px' }} onClick={() => setIsDrawerOpen(false)}>
              Batal
            </button>
            <button type='submit' form='kt_call_result_form' className='btn btn-primary fw-bold px-6 px-md-10 shadow-sm'
              style={{ borderRadius: '12px' }} disabled={loading}>
              {!loading ? (
                <><i className='bi bi-check-lg me-2'></i> Simpan Hasil</>
              ) : (
                <span className='spinner-border spinner-border-sm ms-2'></span>
              )}
            </button>
          </div>
        </div>
      </div>
      {isDrawerOpen && <div className='position-fixed top-0 start-0 w-100 h-100 bg-black opacity-25' style={{ zIndex: 9998 }} onClick={() => setIsDrawerOpen(false)} />}

      {/* TEST CALL MODAL */}
      {isTestCallModalOpen && (
        <div className='modal fade show d-block' style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content' style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className='modal-header border-0 bg-light'>
                <h3 className='modal-title fw-bolder'>Mulai Test Call</h3>
                <button className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={() => setIsTestCallModalOpen(false)}>
                  <i className='bi bi-x fs-1'></i>
                </button>
              </div>
              <div className='modal-body px-8 py-5'>
                <p className='text-gray-600 mb-6'>Test call akan memanggil semua data leads yang diupload (satu per satu secara background) tanpa mengubah status asli leads.</p>
                <div className='mb-5'>
                  <label className='form-label fw-bold'>Timeout (Detik)</label>
                  <select className='form-select form-select-solid' value={testCallTimeout} onChange={(e) => setTestCallTimeout(Number(e.target.value))}>
                    <option value={15}>15 Detik</option>
                    <option value={30}>30 Detik</option>
                    <option value={45}>45 Detik</option>
                    <option value={60}>60 Detik</option>
                  </select>
                </div>
              </div>
              <div className='modal-footer border-0 pt-0'>
                <button type='button' className='btn btn-light' onClick={() => setIsTestCallModalOpen(false)} disabled={isTestCalling}>Batal</button>
                <button type='button' className='btn btn-primary d-flex align-items-center' onClick={handleTestCallSubmit} disabled={isTestCalling}>
                  {isTestCalling ? <span className='spinner-border spinner-border-sm me-2'></span> : <i className='bi bi-telephone-outbound me-2'></i>}
                  Mulai Test
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TRANSFER LEADS MODAL */}
      {isTransferModalOpen && (
        <div className='modal fade show d-block' style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content' style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className='modal-header border-0 bg-light'>
                <h3 className='modal-title fw-bolder'>Transfer Data Leads</h3>
                <button className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={() => setIsTransferModalOpen(false)}>
                  <i className='bi bi-x fs-1'></i>
                </button>
              </div>
              <div className='modal-body px-8 py-5'>
                <p className='text-gray-600 mb-6'>Salin data leads dari campaign ini ke campaign lain dengan status default (Ready).</p>
                <div className='mb-5'>
                  <label className='form-label fw-bold'>Campaign Tujuan</label>
                  <select
                    className='form-select form-select-solid'
                    value={transferTargetId}
                    onChange={(e) => setTransferTargetId(e.target.value)}
                  >
                    <option value=''>-- Pilih Campaign --</option>
                    {availableCampaigns.map((c: any) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className='mb-5'>
                  <label className='form-label fw-bold'>Filter Status (Opsional)</label>
                  <select
                    className='form-select form-select-solid'
                    value={transferStatusFilter}
                    onChange={(e) => setTransferStatusFilter(e.target.value)}
                  >
                    <option value=''>Semua Status</option>
                    <option value='failed'>Failed (Gagal Dihubungi)</option>
                    <option value='close'>Close (Selesai)</option>
                    <option value='ready'>Ready</option>
                  </select>
                  <div className='text-muted mt-2 fs-8'>Hanya lead dengan status ini yang akan ditransfer. Biarkan 'Semua Status' untuk mentransfer seluruh lead.</div>
                </div>
              </div>
              <div className='modal-footer border-0 pt-0'>
                <button type='button' className='btn btn-light' onClick={() => setIsTransferModalOpen(false)} disabled={isTransferring}>Batal</button>
                <button type='button' className='btn btn-primary d-flex align-items-center' onClick={handleTransferSubmit} disabled={isTransferring || !transferTargetId}>
                  {isTransferring ? <span className='spinner-border spinner-border-sm me-2'></span> : <i className='bi bi-arrow-left-right me-2'></i>}
                  Transfer Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .sticky-column { 
          position: sticky !important; 
          z-index: 1; 
          background-color: inherit;
        }
        thead th.sticky-column { background-color: var(--bs-body-bg) !important; }
        tbody td.sticky-column { background-color: var(--bs-body-bg) !important; }
        .pagination .page-item.active .page-link { background-color: var(--bs-primary) !important; color: white !important; }
        .scroll-y::-webkit-scrollbar { width: 5px; }
        .scroll-y::-webkit-scrollbar-thumb { background: var(--bs-gray-300); border-radius: 10px; }
        .nav-line-tabs .nav-item .nav-link.active { border-bottom: 3px solid var(--bs-primary) !important; color: var(--bs-primary) !important; }
      `}</style>
    </div>
  )
}
